<template>
  <div class="container-fluid run-detail-page">
    <div class="run-detail-header pb-3">
      <div class="d-flex align-items-center mb-1">
        <router-link :to="runBackTarget" class="text-secondary text-sm me-2">
          <i class="fas fa-arrow-left me-1"></i> Test Run
        </router-link>
      </div>
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <h4 class="mb-0 font-weight-bold">{{ run.name || "—" }}</h4>
          <p class="text-secondary text-sm mb-0">{{ run.subProjectName || "" }}</p>
        </div>
        <div class="d-flex align-items-center gap-2 run-header-actions">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary mb-0 run-export-btn"
            :disabled="loading"
            @click.stop.prevent="openRunView"
          >
            <i class="fas fa-eye run-export-btn__icon me-1 text-success"></i> View
          </button>
          <button class="btn btn-sm btn-outline-secondary mb-0 run-export-btn" @click="generatePDF">
            <i class="fas fa-file-pdf run-export-btn__icon me-1 text-danger"></i> Export PDF
          </button>
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary mb-0 run-export-btn"
            :disabled="loading || renumberingRunIds || !detailCases.length"
            @click="openRenumberConfirm"
          >
            {{ renumberingRunIds ? "Renumbering..." : "Renumber Run IDs" }}
          </button>
        </div>
      </div>
    </div>

    <div class="row pb-2 run-detail-main">
      <div class="col-12 run-detail-col">
        <div class="card run-detail-card">
          <div class="card-header pb-3 pt-3 border-bottom run-summary-header">
            <button
              type="button"
              class="run-filter-toggle"
              :aria-expanded="filtersPanelOpen ? 'true' : 'false'"
              :aria-label="filtersPanelOpen ? 'Hide filters' : 'Show filters'"
              @click="filtersPanelOpen = !filtersPanelOpen"
            >
              <span class="run-filter-toggle-tab">
                Filter
                <i class="fas" :class="filtersPanelOpen ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              </span>
            </button>
            <div class="row g-3">
              <div class="col">
                <div class="summary-card">
                  <div class="summary-card-body">
                    <p class="summary-card-label">Total Test Case</p>
                    <p class="summary-card-value summary-value-total">{{ allCases.length }}</p>
                  </div>
                </div>
              </div>
              <div class="col">
                <div class="summary-card">
                  <div class="summary-card-body">
                    <p class="summary-card-label">Passed</p>
                    <p class="summary-card-value summary-value-pass">{{ countStatus('Pass') }}</p>
                  </div>
                </div>
              </div>
              <div class="col">
                <div class="summary-card">
                  <div class="summary-card-body">
                    <p class="summary-card-label">Pass with Condition</p>
                    <p class="summary-card-value summary-value-condition">{{ countStatus('Pass with Condition') }}</p>
                  </div>
                </div>
              </div>
              <div class="col">
                <div class="summary-card">
                  <div class="summary-card-body">
                    <p class="summary-card-label">Issue / Failed</p>
                    <p class="summary-card-value summary-value-fail">{{ countFailedAndBlocked() }}</p>
                  </div>
                </div>
              </div>
              <div class="col">
                <div class="summary-card">
                  <div class="summary-card-body">
                    <p class="summary-card-label">No Run</p>
                    <p class="summary-card-value summary-value-norun">{{ countStatus('No Run') }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Test Cases Table -->
          <div class="card-body px-0 pt-0 pb-2 run-detail-card-body">
            <div v-if="loading" class="text-center py-5">
              <i class="fas fa-spinner fa-spin fa-2x text-secondary"></i>
              <p class="text-secondary text-sm mt-2 mb-0">Loading...</p>
            </div>
            <div v-else-if="loadError" class="text-center py-5 px-3">
              <i class="fas fa-exclamation-circle fa-2x text-danger opacity-8"></i>
              <p class="text-danger text-sm mt-2 mb-3">{{ loadError }}</p>
              <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="loadAll">Retry</button>
            </div>
            <div v-else class="run-detail-list-shell">
              <div class="run-search-bar px-3" :class="{ 'run-search-bar--collapsed': !filtersPanelOpen }">
                <div v-if="filtersPanelOpen" class="run-filter-toolbar">
                  <div class="run-filter-search">
                    <label class="text-xs font-weight-bold text-secondary mb-1 d-block run-filter-search-label">Search</label>
                    <div class="run-search-input-wrap">
                      <i class="fas fa-search run-search-input-icon" aria-hidden="true"></i>
                      <input
                        v-model="search"
                        type="text"
                        class="run-search-input-field"
                        placeholder="Search test case..."
                      />
                      <button
                        v-if="search"
                        type="button"
                        class="run-search-clear-btn"
                        aria-label="Clear search"
                        @click="search = ''"
                      >
                        <i class="fas fa-times" aria-hidden="true"></i>
                      </button>
                    </div>
                  </div>
                  <div class="run-filter-field run-filter-field--function">
                    <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Function</label>
                    <base-select
                      size="sm"
                      :model-value="filters.function"
                      :options="functionFilterSelectOptions"
                      option-label="label"
                      option-value="value"
                      @update:model-value="(v) => setRunFilterValue('function', v ?? '')"
                    />
                  </div>
                  <div class="run-filter-field run-filter-field--scenario">
                    <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Scenario</label>
                    <base-select
                      size="sm"
                      :model-value="filters.scenario"
                      :options="scenarioFilterSelectOptions"
                      option-label="label"
                      option-value="value"
                      @update:model-value="(v) => setRunFilterValue('scenario', v ?? '')"
                    />
                  </div>
                  <div class="run-filter-field run-filter-field--sub-scenario">
                    <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Sub-Scenario</label>
                    <base-select
                      size="sm"
                      :model-value="filters.subScenario"
                      :options="subScenarioFilterSelectOptions"
                      option-label="label"
                      option-value="value"
                      @update:model-value="(v) => setRunFilterValue('subScenario', v ?? '')"
                    />
                  </div>
                  <div class="run-filter-field run-filter-field--status">
                    <label class="text-xs font-weight-bold text-secondary mb-1 d-block">Status</label>
                    <base-select
                      size="sm"
                      :model-value="filters.status"
                      :options="statusFilterSelectOptions"
                      option-label="label"
                      option-value="value"
                      @update:model-value="(v) => setRunFilterValue('status', v ?? '')"
                    />
                  </div>
                  <div class="run-filter-field run-filter-field--clear">
                    <label class="text-xs font-weight-bold text-secondary mb-1 d-block run-filter-search-label">Clear</label>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary mb-0 run-clear-filters-btn"
                      :disabled="!hasActiveRunFilters"
                      @click="clearRunFilters"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
              <div class="run-detail-table-wrap">
              <div class="run-table">
                <div class="run-table-header">
                  <span class="run-col-id">ID</span>
                  <span class="run-col-name">TEST CASE NAME</span>
                  <span class="run-col-type text-center">TYPE</span>
                  <span class="run-col-priority text-center">PRIORITY</span>
                  <span class="run-col-actual text-center">ACTUAL RESULT</span>
                  <span class="run-col-result text-center">RESULT</span>
                </div>
                <div class="run-table-body">
                <div v-if="!groupedFilteredCases.length" class="run-table-empty">
                  <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
                  <p class="text-secondary text-sm mt-2 mb-0">{{ emptyCasesMessage }}</p>
                </div>
                <template v-for="fnGroup in groupedFilteredCases" :key="fnGroup.function">
                  <div class="run-function-row" style="cursor:pointer;" @click="toggleFunction(fnGroup.function)">
                    <i class="fas me-1 text-secondary" style="font-size:0.65rem; width:12px;"
                      :class="collapsedFunctions.includes(fnGroup.function) ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
                    <i class="fas me-2 text-primary"
                      :class="collapsedFunctions.includes(fnGroup.function) ? 'fa-folder' : 'fa-folder-open'"></i>
                    <span class="text-xs font-weight-bold text-dark run-function-label">{{ fnGroup.function || '(No Function)' }}</span>
                    <span class="run-group-count">({{ functionCaseCount(fnGroup) }})</span>
                  </div>
                  <template v-if="!collapsedFunctions.includes(fnGroup.function)">
                <template v-for="scGroup in fnGroup.scenarioGroups" :key="fnGroup.function + '|' + scGroup.scenario">
                  <div class="run-scenario-row" style="cursor:pointer;" @click="toggleScenario(fnGroup.function, scGroup.scenario)">
                    <i class="fas me-1 text-secondary" style="font-size:0.65rem; width:12px;"
                      :class="collapsedScenarios.includes(scenarioCollapseKey(fnGroup.function, scGroup.scenario)) ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
                    <i class="fas me-2 text-warning"
                      :class="collapsedScenarios.includes(scenarioCollapseKey(fnGroup.function, scGroup.scenario)) ? 'fa-folder' : 'fa-folder-open'"></i>
                    <span class="text-xs font-weight-bold text-dark">{{ scGroup.scenario || '(No Scenario)' }}</span>
                  </div>
                  <template v-if="!collapsedScenarios.includes(scenarioCollapseKey(fnGroup.function, scGroup.scenario))">
                    <template v-for="subGroup in scGroup.subGroups" :key="subGroup.subScenario">
                      <div class="run-sub-scenario-row" style="cursor:pointer;"
                        @click="toggleSubScenario(fnGroup.function, scGroup.scenario, subGroup.subScenario)">
                        <i class="fas me-2 text-secondary"
                          :class="collapsedSubScenarios.includes(subScenarioCollapseKey(fnGroup.function, scGroup.scenario, subGroup.subScenario)) ? 'fa-angle-right' : 'fa-angle-down'"></i>
                        <span class="text-xs text-secondary run-sub-scenario-label">{{ subGroup.subScenario || '(No Sub-Scenario)' }}</span>
                      </div>
                      <template v-if="!collapsedSubScenarios.includes(subScenarioCollapseKey(fnGroup.function, scGroup.scenario, subGroup.subScenario))">
                        <div v-for="tc in subGroup.cases" :key="tc.key" class="run-row">
                          <div class="run-col-id">
                            <span class="text-xs text-secondary font-weight-bold">{{ tc.tcLabel }}</span>
                            <span
                              v-if="tc.testType"
                              class="run-type-pill"
                              :class="testTypeToneClass(tc.testType)"
                            >
                              {{ tc.testType }}
                            </span>
                          </div>
                          <div class="run-col-name">
                            <p class="tc-name mb-0">{{ tc.title }}</p>
                            <p v-if="tc.description" class="tc-sub mb-0">{{ tc.description }}</p>
                          </div>
                          <div class="run-col-type text-center">
                            <span class="text-xs text-secondary">{{ tc.typeOfTestCase || '—' }}</span>
                              </div>
                          <div class="run-col-priority text-center">
                            <span class="text-xs text-secondary">{{ tc.priority || '—' }}</span>
                            </div>
                          <div class="run-col-actual text-center">
                            <button
                              class="btn btn-sm btn-outline-secondary mb-0 run-actual-btn"
                              :class="actualBtnClass(tc.runStatus)"
                              @click="openActualModal(tc)"
                            >
                              <i v-if="tc.hasActualResult" class="fas fa-file-alt run-actual-btn__icon me-1"></i>{{ actualResultBtnLabel(tc) }}
                            </button>
                          </div>
                          <div class="run-col-result text-center">
                            <span
                              class="result-label"
                              :class="[
                                resultLabelClass(tc.runStatus),
                                tc.runStatus === 'Pass with Condition' ? 'result-label--pass-condition-font' : '',
                              ]"
                            >{{ resultDisplayLabel(tc.runStatus) }}</span>
                          </div>
                            </div>
                    </template>
                  </template>
                  </template>
                </template>
                  </template>
                </template>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actual Result Modal -->
    <Teleport to="body">
      <div v-if="showActualModal && modalTc" class="modal-overlay">
        <div class="modal-box run-actual-modal-box">
          <div class="d-flex align-items-center p-3 border-bottom run-actual-modal-header">
            <div>
              <h6 class="mb-0 font-weight-bold">{{ modalTc.title }}</h6>
              <p class="text-xs text-secondary mb-0">{{ modalTc.scenario }} › {{ modalTc.subScenario }}</p>
            </div>
          </div>
          <div class="run-actual-modal-controls">
            <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
              <div class="d-flex align-items-center gap-4 flex-wrap">
                <div class="d-flex align-items-center gap-2">
                  <label class="text-xs font-weight-bold mb-0">Result Mode</label>
                  <base-select
                    v-model="modalTc.actualResultMode"
                    size="sm"
                    :options="actualResultModeOptions"
                    option-label="label"
                    option-value="value"
                    style="width:180px"
                  />
                </div>
                <div class="d-flex align-items-center gap-2">
                  <label class="text-xs font-weight-bold mb-0">Result</label>
                  <div
                    class="run-result-select"
                    :class="resultSelectClass(modalTc.runStatus)"
                    style="width:175px"
                  >
                    <base-select
                      v-model="modalTc.runStatus"
                      size="sm"
                      :options="runStatusOptions"
                      option-label="label"
                      option-value="value"
                    />
                  </div>
                </div>
              </div>
              <span class="run-actual-ref-id">Ref: {{ formatLibraryTcLabel(modalTc) }}</span>
            </div>
          </div>
          <div class="run-actual-modal-body">
            <h6 v-if="modalTc.actualResultMode !== 'byStep'" class="font-weight-bold mb-3" style="font-size:0.9rem;">Steps and Results</h6>

            <div v-if="loadingSteps" class="text-center py-3">
              <i class="fas fa-spinner fa-spin text-secondary me-1"></i>
              <span class="text-secondary text-xs">Loading steps...</span>
            </div>

            <!-- By Step mode -->
            <div v-if="modalTc.actualResultMode === 'byStep'" class="run-actual-panel-box p-3 rounded run-actual-bystep-tabs">
              <div class="tab-shell" role="tablist" aria-label="Steps and history">
                <div
                  class="tab-item"
                  :class="{ active: actualModalSideTab === 'result' }"
                  role="tab"
                  :aria-selected="actualModalSideTab === 'result'"
                  @click="actualModalSideTab = 'result'"
                >
                  Steps and Results
                </div>
                <div
                  class="tab-item"
                  :class="{ active: actualModalSideTab === 'history' }"
                  role="tab"
                  :aria-selected="actualModalSideTab === 'history'"
                  @click="openActualHistoryTab"
                >
                  History
                </div>
              </div>
              <div v-show="actualModalSideTab === 'result'" class="tp-side-tab-panel">
            <div class="table-responsive run-step-modal-wrap">
              <table class="table table-sm align-items-center mb-0 run-step-modal-table">
                <colgroup>
                  <col style="width:4%;" /><col style="width:10%;" /><col style="width:24%;" />
                  <col style="width:24%;" /><col style="width:24%;" /><col style="width:14%;" />
                </colgroup>
                <thead>
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">#</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Platform</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Step</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Expected Result</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Actual Result</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Image / Video</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="modalTc.steps.length === 0">
                    <td colspan="6" class="text-center text-secondary text-xs py-3">No steps defined.</td>
                  </tr>
                  <tr v-for="(step, si) in modalTc.steps" :key="si">
                    <td class="text-xs text-secondary font-weight-bold align-top pt-3">{{ step.stepNo || si + 1 }}</td>
                    <td class="align-top pt-2">
                      <span class="text-xs text-secondary">{{ step.platform }}</span>
                    </td>
                    <td class="align-top pt-2 run-step-col">
                      <div class="run-step-cell-inner">
                        <BulletText :text="step.stepDetail" list-class="bullet-text mb-0 ps-3 text-xs" text-class="text-xs" />
                      </div>
                    </td>
                    <td class="align-top pt-2 run-expected-col">
                      <div class="run-step-cell-inner">
                        <BulletText :text="step.expectedResult" list-class="bullet-text mb-0 ps-3 text-xs" text-class="text-xs" />
                      </div>
                    </td>
                    <td class="align-top pt-2 run-actual-col">
                      <textarea v-model="step.actualResult" class="form-control form-control-sm run-actual-textarea" rows="3"
                        placeholder="Actual Result"></textarea>
                    </td>
                    <td class="align-top pt-2">
                      <label class="btn btn-sm btn-outline-secondary mb-0 run-step-upload-btn" :class="{ disabled: isMediaUploadBusy }">
                        <i class="fas fa-upload me-1"></i> Upload
                        <input type="file" :accept="mediaAccept" multiple class="d-none" :disabled="isMediaUploadBusy" @change="handleStepImage(step, si, $event)" />
                      </label>
                      <div v-if="stepPendingSlots(stepMediaKey(step, si)).length || (step.actualImages && step.actualImages.length > 0)" class="mt-1 d-flex flex-wrap gap-1">
                        <div v-for="slot in stepPendingSlots(stepMediaKey(step, si))" :key="slot.id" class="run-media-thumb run-media-thumb--sm">
                          <div class="run-media-thumb__pending">
                            <i :class="slot.isVideo ? 'fas fa-video' : 'fas fa-image'" class="run-media-thumb__type-icon"></i>
                            <span class="run-media-thumb__status text-xxs">{{ pendingSlotLabel(slot) }}</span>
                            <div class="run-media-upload-progress__bar">
                              <div
                                class="run-media-upload-progress__fill"
                                :class="{ 'run-media-upload-progress__fill--indeterminate': slot.percent == null }"
                                :style="pendingSlotBarStyle(slot)"
                              ></div>
                            </div>
                            <span v-if="slot.percent != null" class="run-media-thumb__percent text-xxs">{{ slot.percent }}%</span>
                          </div>
                        </div>
                        <div v-for="(img, idx) in step.actualImages" :key="'step-media-' + idx" class="run-media-thumb run-media-thumb--sm position-relative">
                          <template v-if="isVideoMediaUrl(img)">
                            <video
                              :src="img"
                              class="run-media-thumb__video"
                              preload="metadata"
                              muted
                              playsinline
                              @loadeddata="onVideoMediaLoaded('step-' + stepMediaKey(step, si), idx)"
                              @click="openLightbox(img)"
                              @error="refreshStepImage(step, si, idx)"
                            />
                            <div v-if="!isVideoMediaLoaded('step-' + stepMediaKey(step, si), idx)" class="run-media-thumb__overlay">
                              <i class="fas fa-spinner fa-spin run-media-thumb__overlay-icon"></i>
                              <span class="text-xxs">Loading…</span>
                            </div>
                            <div v-else class="run-media-thumb__play-badge">
                              <i class="fas fa-play"></i>
                            </div>
                          </template>
                          <img
                            v-else
                            :src="img"
                            class="run-media-thumb__image"
                            @click="openLightbox(img)"
                            @error="refreshStepImage(step, si, idx)"
                          />
                          <button type="button" @click="removeStepImage(step, idx)"
                            class="position-absolute top-0 end-0 btn btn-sm p-0 run-media-thumb__remove"
                            style="width:16px;height:16px;background:#f5365c;border-radius:50%;line-height:1;font-size:0.55rem;color:white;border:none;">×</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
              </div>
              <div v-show="actualModalSideTab === 'history'" class="tp-side-tab-panel run-actual-history-panel">
              <div class="run-actual-history-scroll">
                <div v-if="actualResultHistoryLoading" class="text-secondary text-xs py-2">Loading...</div>
                <div v-else-if="actualResultHistory.length === 0" class="text-secondary text-xs py-2">No history yet.</div>
                <div v-else class="activity-history-list">
                  <div
                    v-for="entry in actualResultHistory"
                    :key="entry.historyId"
                    class="activity-history-item"
                  >
                    <div class="d-flex justify-content-between gap-2 mb-1">
                      <span class="activity-badge badge-change">Change</span>
                      <span class="text-secondary text-xs text-nowrap">{{ formatActivityTime(entry.createdAt) }}</span>
                    </div>
                    <p class="text-xs text-secondary mb-1">{{ entry.authorName }}</p>
                    <template v-for="(chg, ci) in entry.changes" :key="ci">
                      <div
                        v-if="isHistoryMediaChange(chg)"
                        class="run-history-media-change"
                        :class="{ 'mb-0': ci === entry.changes.length - 1 }"
                      >
                        <strong class="text-sm">{{ chg.fieldName }}</strong>
                        <div class="run-history-media-compare">
                          <div class="run-history-media-side">
                            <span class="text-xxs text-secondary">Previous</span>
                            <div v-if="historyMediaPreview(chg.oldValue).length" class="qa-tr-view-media">
                              <button
                                v-for="item in historyMediaPreview(chg.oldValue)"
                                :key="'old-' + item.index"
                                type="button"
                                class="qa-tr-view-media-thumb"
                                @click="openHistoryMedia(chg.oldValue, item.index)"
                              >
                                <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                <img v-else :src="item.src" alt="" />
                                <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                  <i class="fas fa-play"></i>
                                </span>
                                <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                              </button>
                            </div>
                            <span v-else class="text-xs text-secondary">—</span>
                          </div>
                          <span class="text-secondary">→</span>
                          <div class="run-history-media-side">
                            <span class="text-xxs text-secondary">Current</span>
                            <div v-if="historyMediaPreview(chg.newValue).length" class="qa-tr-view-media">
                              <button
                                v-for="item in historyMediaPreview(chg.newValue)"
                                :key="'new-' + item.index"
                                type="button"
                                class="qa-tr-view-media-thumb"
                                @click="openHistoryMedia(chg.newValue, item.index)"
                              >
                                <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                <img v-else :src="item.src" alt="" />
                                <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                  <i class="fas fa-play"></i>
                                </span>
                                <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                              </button>
                            </div>
                            <span v-else class="text-xs text-secondary">—</span>
                          </div>
                        </div>
                      </div>
                      <p
                        v-else
                        class="text-sm mb-1 activity-text"
                        :class="{ 'mb-0': ci === entry.changes.length - 1 }"
                      >
                        <strong>{{ chg.fieldName }}</strong>:
                        <span class="text-secondary">{{ formatHistoryValue(chg.fieldName, chg.oldValue) }}</span>
                        →
                        <span>{{ formatHistoryValue(chg.fieldName, chg.newValue) }}</span>
                      </p>
                    </template>
                  </div>
                </div>
              </div>
              </div>
            </div>

            <!-- Overview mode -->
            <div v-else>
              <div class="table-responsive run-step-modal-wrap mb-4">
                <table class="table table-sm align-items-center mb-0 run-step-modal-table">
                  <colgroup>
                    <col style="width:5%;" /><col style="width:12%;" />
                    <col style="width:41%;" /><col style="width:42%;" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">#</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Platform</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Step</th>
                      <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Expected Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="modalTc.steps.length === 0">
                      <td colspan="4" class="text-center text-secondary text-xs py-3">No steps defined.</td>
                    </tr>
                    <tr v-for="(step, si) in modalTc.steps" :key="si">
                      <td class="text-xs text-secondary font-weight-bold align-top pt-3">{{ step.stepNo || si + 1 }}</td>
                      <td class="align-top pt-2"><span class="text-xs text-secondary">{{ step.platform }}</span></td>
                      <td class="align-top pt-2 run-step-col">
                        <div class="run-step-cell-inner">
                          <BulletText :text="step.stepDetail" list-class="bullet-text mb-0 ps-3 text-xs" text-class="text-xs" />
                        </div>
                      </td>
                      <td class="align-top pt-2 run-expected-col">
                        <div class="run-step-cell-inner">
                          <BulletText :text="step.expectedResult" list-class="bullet-text mb-0 ps-3 text-xs" text-class="text-xs" />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="run-actual-panel-box p-3 rounded">
                <div class="tab-shell" role="tablist" aria-label="Actual result panel">
                  <div
                    class="tab-item"
                    :class="{ active: actualModalSideTab === 'result' }"
                    role="tab"
                    :aria-selected="actualModalSideTab === 'result'"
                    @click="actualModalSideTab = 'result'"
                  >
                    Actual Result
                  </div>
                  <div
                    class="tab-item"
                    :class="{ active: actualModalSideTab === 'history' }"
                    role="tab"
                    :aria-selected="actualModalSideTab === 'history'"
                    @click="openActualHistoryTab"
                  >
                    History
                  </div>
                </div>

                <div v-show="actualModalSideTab === 'result'" class="tp-side-tab-panel">
                <div class="mb-3">
                  <label class="text-xs font-weight-bold mb-1 d-block">Actual Result</label>
                  <textarea v-model="modalTc.actualResult" class="form-control form-control-sm" rows="3"
                    style="font-size:0.8rem;" placeholder="Enter overall actual result..."></textarea>
                </div>
                <div>
                  <label class="text-xs font-weight-bold mb-1 d-block">Actual Result Images / Videos</label>
                  <label class="btn btn-sm btn-outline-secondary mb-0" :class="{ disabled: isMediaUploadBusy }" style="font-size:0.75rem; cursor:pointer;">
                    <i class="fas fa-upload me-1"></i> Upload Image / Video
                    <input type="file" :accept="mediaAccept" multiple class="d-none" :disabled="isMediaUploadBusy" @change="handleOverviewImage($event)" />
                  </label>
                  <div v-if="overviewPendingSlots.length || (modalTc.actualResultImages && modalTc.actualResultImages.length > 0)" class="d-flex flex-wrap gap-2 mt-2">
                    <div v-for="slot in overviewPendingSlots" :key="slot.id" class="run-media-thumb">
                      <div class="run-media-thumb__pending">
                        <i :class="slot.isVideo ? 'fas fa-video' : 'fas fa-image'" class="run-media-thumb__type-icon"></i>
                        <span class="run-media-thumb__status text-xxs">{{ pendingSlotLabel(slot) }}</span>
                        <div class="run-media-upload-progress__bar">
                          <div
                            class="run-media-upload-progress__fill"
                            :class="{ 'run-media-upload-progress__fill--indeterminate': slot.percent == null }"
                            :style="pendingSlotBarStyle(slot)"
                          ></div>
                        </div>
                        <span v-if="slot.percent != null" class="run-media-thumb__percent text-xxs">{{ slot.percent }}%</span>
                      </div>
                    </div>
                    <div v-for="(img, idx) in modalTc.actualResultImages" :key="'overview-media-' + idx" class="run-media-thumb position-relative">
                      <template v-if="isVideoMediaUrl(img)">
                        <video
                          :src="img"
                          class="run-media-thumb__video"
                          preload="metadata"
                          muted
                          playsinline
                          @loadeddata="onVideoMediaLoaded('overview', idx)"
                          @click="openLightbox(img)"
                          @error="refreshOverviewImage(idx)"
                        />
                        <div v-if="!isVideoMediaLoaded('overview', idx)" class="run-media-thumb__overlay">
                          <i class="fas fa-spinner fa-spin run-media-thumb__overlay-icon"></i>
                          <span class="text-xxs">Loading video…</span>
                        </div>
                        <div v-else class="run-media-thumb__play-badge">
                          <i class="fas fa-play"></i>
                        </div>
                      </template>
                      <img
                        v-else
                        :src="img"
                        class="run-media-thumb__image"
                        @click="openLightbox(img)"
                        @error="refreshOverviewImage(idx)"
                      />
                      <button type="button" @click="removeOverviewImage(idx)"
                        class="position-absolute top-0 end-0 btn btn-sm p-0 run-media-thumb__remove"
                        style="width:18px;height:18px;background:#f5365c;border-radius:50%;line-height:1;font-size:0.6rem;color:white;border:none;">×</button>
                    </div>
                  </div>
                </div>
                <div v-if="modalTcJiraItems.length > 0 || loadingModalJira" class="mt-3 pt-3 border-top">
                  <label class="text-xs font-weight-bold mb-2 d-block">Linked Jira</label>
                  <div v-if="loadingModalJira" class="text-xs text-secondary">
                    <i class="fas fa-spinner fa-spin me-1"></i>Loading...
              </div>
                  <div v-else class="table-responsive">
                    <table class="table table-sm mb-0 tc-jira-links-table">
                      <thead>
                        <tr>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style="width:22%;">Jira ID</th>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Jira Title</th>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style="width:24%;">Jira Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="j in modalTcJiraItems" :key="j.key">
                          <td class="align-middle">
                            <a :href="j.url" target="_blank" rel="noopener" class="text-xs font-weight-bold text-primary">
                              <i class="fab fa-jira me-1"></i>{{ j.key }}
                              <i class="fas fa-external-link-alt ms-1" style="font-size:0.6rem;"></i>
                            </a>
                          </td>
                          <td class="align-middle text-xs">{{ j.summary || '—' }}</td>
                          <td class="align-middle">
                            <jira-status-select
                              :issue-key="j.key"
                              :status="j.status || ''"
                              :transitions="j.transitions || []"
                              compact
                              @update:status="j.status = $event"
                              @update:transitions="j.transitions = $event"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
            </div>
          </div>
              </div>

                <div v-show="actualModalSideTab === 'history'" class="tp-side-tab-panel run-actual-history-panel">
                  <div class="run-actual-history-scroll">
                    <div v-if="actualResultHistoryLoading" class="text-secondary text-xs py-2">Loading...</div>
                    <div v-else-if="actualResultHistory.length === 0" class="text-secondary text-xs py-2">No history yet.</div>
                    <div v-else class="activity-history-list">
                      <div
                        v-for="entry in actualResultHistory"
                        :key="entry.historyId"
                        class="activity-history-item"
                      >
                        <div class="d-flex justify-content-between gap-2 mb-1">
                          <span class="activity-badge badge-change">Change</span>
                          <span class="text-secondary text-xs text-nowrap">{{ formatActivityTime(entry.createdAt) }}</span>
              </div>
                        <p class="text-xs text-secondary mb-1">{{ entry.authorName }}</p>
                        <template v-for="(chg, ci) in entry.changes" :key="ci">
                          <div
                            v-if="isHistoryMediaChange(chg)"
                            class="run-history-media-change"
                            :class="{ 'mb-0': ci === entry.changes.length - 1 }"
                          >
                            <strong class="text-sm">{{ chg.fieldName }}</strong>
                            <div class="run-history-media-compare">
                              <div class="run-history-media-side">
                                <span class="text-xxs text-secondary">Previous</span>
                                <div v-if="historyMediaPreview(chg.oldValue).length" class="qa-tr-view-media">
                                  <button
                                    v-for="item in historyMediaPreview(chg.oldValue)"
                                    :key="'old-' + item.index"
                                    type="button"
                                    class="qa-tr-view-media-thumb"
                                    @click="openHistoryMedia(chg.oldValue, item.index)"
                                  >
                                    <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                    <img v-else :src="item.src" alt="" />
                                    <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                      <i class="fas fa-play"></i>
                                    </span>
                                    <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                                  </button>
                                </div>
                                <span v-else class="text-xs text-secondary">—</span>
                              </div>
                              <span class="text-secondary">→</span>
                              <div class="run-history-media-side">
                                <span class="text-xxs text-secondary">Current</span>
                                <div v-if="historyMediaPreview(chg.newValue).length" class="qa-tr-view-media">
                                  <button
                                    v-for="item in historyMediaPreview(chg.newValue)"
                                    :key="'new-' + item.index"
                                    type="button"
                                    class="qa-tr-view-media-thumb"
                                    @click="openHistoryMedia(chg.newValue, item.index)"
                                  >
                                    <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                    <img v-else :src="item.src" alt="" />
                                    <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                      <i class="fas fa-play"></i>
                                    </span>
                                    <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                                  </button>
                                </div>
                                <span v-else class="text-xs text-secondary">—</span>
                              </div>
                            </div>
                          </div>
                          <p
                            v-else
                            class="text-sm mb-1 activity-text"
                            :class="{ 'mb-0': ci === entry.changes.length - 1 }"
                          >
                            <strong>{{ chg.fieldName }}</strong>:
                            <span class="text-secondary">{{ formatHistoryValue(chg.fieldName, chg.oldValue) }}</span>
                            →
                            <span>{{ formatHistoryValue(chg.fieldName, chg.newValue) }}</span>
                          </p>
                        </template>
              </div>
            </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="modalTc.actualResultMode === 'byStep' && (modalTcJiraItems.length > 0 || loadingModalJira)"
              v-show="actualModalSideTab === 'result'"
              class="mt-3"
            >
              <div class="tc-jira-links-box">
                <label class="text-xs font-weight-bold mb-2 d-block">Linked Jira</label>
                <div v-if="loadingModalJira" class="text-xs text-secondary">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading...
                </div>
                <div v-else class="table-responsive">
                  <table class="table table-sm mb-0 tc-jira-links-table">
                    <thead>
                      <tr>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style="width:22%;">Jira ID</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Jira Title</th>
                        <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style="width:24%;">Jira Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="j in modalTcJiraItems" :key="j.key">
                        <td class="align-middle">
                          <a :href="j.url" target="_blank" rel="noopener" class="text-xs font-weight-bold text-primary">
                            <i class="fab fa-jira me-1"></i>{{ j.key }}
                            <i class="fas fa-external-link-alt ms-1" style="font-size:0.6rem;"></i>
                          </a>
                        </td>
                        <td class="align-middle text-xs">{{ j.summary || '—' }}</td>
                        <td class="align-middle">
                          <jira-status-select
                            :issue-key="j.key"
                            :status="j.status || ''"
                            :transitions="j.transitions || []"
                            compact
                            @update:status="j.status = $event"
                            @update:transitions="j.transitions = $event"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

          <!-- Create Jira Issue — Failed/Blocked (or keep visible when Jira already linked) -->
          <div
            v-if="showJiraIssuePanel"
            class="jira-issue-panel mb-3"
          >
            <div class="jira-issue-panel__header">
              <div>
                <h6 class="jira-issue-panel__title mb-1">Jira Issue</h6>
                <span class="jira-issue-panel__meta text-xs text-secondary">
                  <template v-if="jiraCreateMode === 'legacy'">
                    Project
                    <span class="jira-issue-panel__project">{{ jiraProjectKeyForIssue || '—' }}</span>
                  </template>
                  <template v-else>
                    Create Bug on space sprint — optional task link
                  </template>
              </span>
            </div>
            </div>

            <div v-if="showJiraLinkForm" class="jira-issue-form">


              <div class="mb-2 mt-2">
                <label class="jira-field-label">Space</label>
                <base-select
                  v-model="jiraLinkSpaceKey"
                  :options="jiraSpaces"
                  option-label="label"
                  option-value="key"
                  placeholder="— Select space —"
                  size="sm"
                  searchable
                  search-placeholder="ค้นหา space…"
                  :disabled="loadingJiraSpaces"
                  empty-label="ไม่พบ space"
                  @change="onJiraLinkSpaceChange"
                />
                <span v-if="loadingJiraSpaces" class="text-xs text-secondary d-block mt-1">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading spaces…
                </span>
              </div>
              <div v-if="jiraLinkSpaceKey" class="mb-2">
                <label class="jira-field-label">Issue / Task</label>
                <base-select
                  v-model="jiraLinkSelectedIssueKey"
                  :options="jiraLinkSpaceIssues"
                  option-label="label"
                  option-value="key"
                  placeholder="— Select issue —"
                  size="sm"
                  searchable
                  search-placeholder="ค้นหา issue…"
                  :disabled="loadingJiraLinkIssues"
                  empty-label="ไม่พบ issue"
                  @change="onJiraLinkIssuePicked"
                />
                <span v-if="loadingJiraLinkIssues" class="text-xs text-secondary d-block mt-1">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading issues…
                </span>
          </div>

              <div class="jira-link-or-divider my-3">
                <span>OR</span>
              </div>

              <div class="mb-2">
                <label class="jira-field-label">Jira Issue Key</label>
                <input
                  v-model="jiraLinkIssueKey"
                  type="text"
                  class="form-control form-control-sm jira-field-input"
                  placeholder="e.g. PROJ-123"
                  @input="onJiraLinkKeyInput"
                  @blur="previewJiraLinkIssue"
                  @keyup.enter="previewJiraLinkIssue"
                />
              </div>

              <div v-if="jiraLinkPreviewLoading" class="text-xs text-secondary mb-2">
                <i class="fas fa-spinner fa-spin me-1"></i>กำลังตรวจสอบ issue…
              </div>
              <div v-else-if="jiraLinkPreview" class="jira-link-preview mb-3">
                <span class="text-xs font-weight-bold">{{ jiraLinkPreview.key }}</span>
                <span class="text-xs text-secondary ms-2">{{ jiraLinkPreview.summary || '—' }}</span>
                <span v-if="jiraLinkPreview.status" class="text-xs text-secondary ms-1">({{ jiraLinkPreview.status }})</span>
              </div>
              <div v-else-if="jiraLinkPreviewError" class="text-xs text-danger mb-2">{{ jiraLinkPreviewError }}</div>

              <div class="d-flex justify-content-end gap-2 flex-wrap">
                <button type="button" class="btn btn-sm btn-outline-secondary mb-0 jira-btn" @click="closeJiraLinkForm">
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-sm bg-gradient-dark text-white mb-0 jira-btn"
                  :disabled="jiraLinkSubmitting || !canSubmitJiraLink"
                  @click="linkExistingJiraIssue"
                >
                  <i class="fas fa-spinner fa-spin me-1" v-if="jiraLinkSubmitting"></i>
                  <i class="fas fa-link me-1" v-else></i>Link Issue
                </button>
              </div>
            </div>

            <div v-if="showJiraCreateForm" class="jira-issue-form">
              <div v-if="jiraCreateMode === 'standalone'" class="mb-2">
                <label class="jira-field-label">Space <span class="text-danger">*</span></label>
                <base-select
                  v-model="jiraIssueForm.spaceKey"
                  :options="jiraSpaces"
                  option-label="label"
                  option-value="key"
                  placeholder="— Select space —"
                  size="sm"
                  searchable
                  search-placeholder="ค้นหา space…"
                  :disabled="loadingJiraSpaces"
                  empty-label="ไม่พบ space"
                  @change="onJiraSpaceChange"
                />
                <span v-if="loadingJiraSpaces" class="text-xs text-secondary d-block mt-1">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading spaces…
                </span>
                <p v-else-if="jiraIssueForm.spaceKey && activeSpaceSprint" class="text-xs text-secondary mb-0 mt-1">
                  Active sprint: <strong>{{ activeSpaceSprint.name }}</strong>
                </p>
                <p v-else-if="jiraIssueForm.spaceKey && !loadingSpaceSprint && !activeSpaceSprint" class="text-xs text-warning mb-0 mt-1">
                  No active sprint on this space
                </p>
                <p v-if="jiraIssueForm.spaceKey" class="text-xs text-secondary mb-0 mt-1">
                  Type: <strong>Bug</strong>
                </p>
              </div>
              <div v-if="jiraCreateMode === 'standalone' && jiraIssueForm.spaceKey" class="mb-2">
                <label class="jira-field-label">Task <span class="text-secondary font-weight-normal">(optional)</span></label>
                <base-select
                  v-model="jiraIssueForm.relatedTaskKey"
                  :options="jiraSpaceTasks"
                  option-label="label"
                  option-value="key"
                  placeholder="— ไม่เลือก task —"
                  size="sm"
                  clearable
                  clear-label="— ไม่เลือก task —"
                  searchable
                  search-placeholder="ค้นหา task…"
                  :disabled="loadingJiraSpaceTasks"
                  empty-label="ไม่พบ task"
                />
                <span v-if="loadingJiraSpaceTasks" class="text-xs text-secondary d-block mt-1">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading tasks…
                </span>
                <p v-else class="text-xs text-secondary mb-0 mt-1">
                  Link Bug กับ task ที่เลือก (Relates) — ถ้าเลือก จะใส่ sprint เดียวกับ task
                </p>
              </div>
              <div class="mb-2">
                <label class="jira-field-label">Assignee</label>
                <base-select
                  v-model="jiraIssueForm.assigneeAccountId"
                  :options="assignableUsers"
                  option-label="displayName"
                  option-value="accountId"
                  placeholder="— ไม่ระบุ —"
                  size="sm"
                  clearable
                  clear-label="— ไม่ระบุ —"
                  searchable
                  search-placeholder="ค้นหาชื่อ…"
                  :disabled="loadingAssignable || !jiraEffectiveProjectKey"
                  empty-label="ไม่พบผู้ใช้"
                />
                <span v-if="loadingAssignable" class="text-xs text-secondary d-block mt-1">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading users…
                </span>
              </div>
              <div class="mb-2">
                <label class="jira-field-label">Issue Title <span class="text-danger">*</span></label>
                <input v-model="jiraIssueForm.title" type="text" class="form-control form-control-sm jira-field-input"
                  placeholder="หัวข้อ issue" />
              </div>
              <div class="mb-2">
                <label class="jira-field-label">Description <span class="text-danger">*</span></label>
                <textarea v-model="jiraIssueForm.description" class="form-control form-control-sm jira-field-input" rows="4"
                  placeholder="รายละเอียด issue" style="resize:vertical;"></textarea>
              </div>
              <div class="mb-3">
                <label class="jira-field-label mb-1 d-block">รูปแนบ</label>
                <label class="btn btn-sm btn-outline-secondary mb-0 jira-btn" style="cursor:pointer;">
                  <i class="fas fa-upload me-1"></i> Upload Image
                  <input type="file" accept="image/*" multiple class="d-none" @change="handleJiraIssueImage($event)" />
                </label>
                <p class="text-xs text-secondary mb-0 mt-1">Attached to the Jira issue (automatically pulled from the Actual Result if available).</p>
                <div v-if="jiraIssueForm.images.length > 0" class="d-flex flex-wrap gap-2 mt-2">
                  <div v-for="(img, idx) in jiraIssueForm.images" :key="'jira-img-' + idx" class="position-relative jira-issue-thumb">
                    <img :src="img" alt="" @click="lightboxSrc = img" />
                    <button type="button" class="jira-issue-thumb__remove" @click="removeJiraIssueImage(idx)">×</button>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-end gap-2 flex-wrap">
                <button type="button" class="btn btn-sm btn-outline-secondary mb-0 jira-btn" @click="closeJiraCreateForm">
                  Cancel
                </button>
                <button
                  type="button"
                  class="btn btn-sm bg-gradient-dark text-white mb-0 jira-btn"
                  :disabled="jiraIssueCreating || !canSubmitJiraIssue"
                  @click="createJiraIssue"
                >
                  <i class="fas fa-spinner fa-spin me-1" v-if="jiraIssueCreating"></i>
                  <i class="fas fa-check me-1" v-else></i>Create in Jira
                </button>
              </div>
            </div>

            <div class="jira-issue-list">
              <div class="jira-issue-list__toolbar">
                <span class="jira-issue-list__label">Issue List</span>
                <div v-if="!showJiraCreateForm && !showJiraLinkForm" class="d-flex gap-2 flex-wrap">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary mb-0 jira-btn"
                    @click="openJiraLinkForm"
                  >
                    <i class="fas fa-link me-1"></i>Link
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm bg-gradient-dark text-white mb-0 jira-btn"
                    @click="openJiraCreateForm"
                  >
                    <i class="fas fa-plus me-1"></i>Create
                  </button>
                </div>
              </div>

              <div v-if="jiraPanelIssueListLoading" class="jira-issue-list__empty">
                <i class="fas fa-spinner fa-spin me-1"></i>กำลังโหลด…
              </div>
              <div v-else-if="jiraPanelIssueList.length === 0" class="jira-issue-list__empty">
                ยังไม่มี issue — กด Link หรือ Create
              </div>
              <div v-else class="table-responsive">
                <table class="table table-sm mb-0 jira-issue-table jira-issue-table--linked">
                  <colgroup>
                    <col class="jira-col jira-col--id" />
                    <col class="jira-col jira-col--type" />
                    <col class="jira-col jira-col--title" />
                    <col class="jira-col jira-col--status" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th class="jira-col jira-col--id">Jira ID</th>
                      <th class="jira-col jira-col--type">Type</th>
                      <th class="jira-col jira-col--title">Task name</th>
                      <th class="jira-col jira-col--status">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, ri) in jiraPanelIssueList" :key="ri + '-' + row.key">
                      <td class="jira-col jira-col--id">
                        <a :href="row.url" target="_blank" rel="noopener" class="jira-issue-link jira-issue-link--new">
                          {{ row.key }}
                        </a>
                      </td>
                      <td class="jira-col jira-col--type">{{ row.issueType || '—' }}</td>
                      <td class="jira-col jira-col--title jira-issue-summary">
                        {{ row.summary || '—' }}
                        <div v-if="row.sprintWarning || row.attachErrors?.length" class="text-xs mt-1">
                          <span v-if="row.sprintWarning" class="text-warning d-block">Sprint: {{ row.sprintWarning }}</span>
                          <span v-if="row.attachErrors?.length" class="text-warning d-block">แนบรูปไม่สำเร็จ</span>
                        </div>
                      </td>
                      <td class="jira-col jira-col--status">
                        <jira-status-select
                          :issue-key="row.key"
                          :status="row.status || ''"
                          :transitions="row.transitions || []"
                          compact
                          @update:status="row.status = $event"
                          @update:transitions="row.transitions = $event"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div v-if="jiraIssueError" class="jira-issue-panel__error">
              <i class="fas fa-exclamation-circle me-1"></i>{{ jiraIssueError }}
            </div>
          </div>
          </div>

          <div class="d-flex align-items-center justify-content-end gap-2 p-3 border-top run-actual-modal-footer">
            <div v-if="saving" class="me-auto run-media-upload-progress run-media-upload-progress--footer">
              <div class="run-media-upload-progress__label text-xs text-secondary">
                <i class="fas fa-spinner fa-spin me-1"></i>{{ savingProgressLabel }}
                <span v-if="mediaUploadState?.scope === 'save' && mediaUploadPercent != null" class="ms-1">{{ mediaUploadPercent }}%</span>
              </div>
              <div v-if="mediaUploadState?.scope === 'save'" class="run-media-upload-progress__bar">
                <div
                  class="run-media-upload-progress__fill"
                  :class="{ 'run-media-upload-progress__fill--indeterminate': mediaUploadPercent == null }"
                  :style="mediaUploadBarStyle"
                ></div>
              </div>
            </div>
            <button class="btn btn-sm btn-outline-secondary mb-0" :disabled="saving || isMediaUploadBusy" @click="showActualModal = false">Close</button>
            <button class="btn btn-sm bg-gradient-dark text-white mb-0" :disabled="saving || isMediaUploadBusy" @click="saveActualResult">
              <span v-if="saving"><i class="fas fa-spinner fa-spin me-1"></i>{{ savingProgressLabel }}</span>
              <span v-else>Save</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Lightbox -->
    <Teleport to="body">
      <div v-if="lightboxSrc" class="lightbox-overlay">
        <button type="button" class="lightbox-close" title="Close" @click="closeLightbox">
          <i class="fas fa-times"></i>
        </button>
        <button
          v-if="lightboxItems.length > 1"
          type="button"
          class="lightbox-nav lightbox-prev"
          title="Previous"
          @click.stop="lightboxPrev"
        >
          <i class="fas fa-chevron-left"></i>
        </button>
        <video v-if="lightboxIsVideo" :src="lightboxSrc" class="lightbox-img" controls autoplay />
        <img v-else :src="lightboxSrc" class="lightbox-img" />
        <button
          v-if="lightboxItems.length > 1"
          type="button"
          class="lightbox-nav lightbox-next"
          title="Next"
          @click.stop="lightboxNext"
        >
          <i class="fas fa-chevron-right"></i>
        </button>
        <span v-if="lightboxItems.length > 1" class="lightbox-count">{{ lightboxIndex + 1 }} / {{ lightboxItems.length }}</span>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showRunView" class="qa-tc-review-overlay qa-tr-view-overlay" :class="runViewMode === 'pdf' ? 'is-pdf' : 'is-standard'">
        <div class="qa-tc-review-shell">
          <div class="qa-tc-review-header">
            <div class="qa-tc-review-header-title">
              <h6 class="mb-0 font-weight-bold">TEST RUN : {{ run.name || "—" }}</h6>
              <p class="text-xs text-secondary mb-0">{{ run.subProjectName || "—" }}</p>
            </div>
            <div class="d-flex align-items-center gap-2">
              <div class="qa-tc-review-mode" role="tablist">
                <button type="button" class="qa-tc-review-mode-btn" :class="{ active: runViewMode === 'standard' }" @click="runViewMode = 'standard'">Standard</button>
                <button type="button" class="qa-tc-review-mode-btn" :class="{ active: runViewMode === 'pdf' }" @click="runViewMode = 'pdf'">Table</button>
              </div>
              <button type="button" class="btn btn-sm btn-secondary mb-0" @click="closeRunView">Close</button>
            </div>
          </div>
          <div class="qa-tc-review-filters">
            <div class="qa-tc-review-filter-field qa-tc-review-filter-field--search">
              <label>Search</label>
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-white border-end-0">
                  <i class="fas fa-search text-secondary" style="font-size:0.7rem;"></i>
                </span>
                <input
                  v-model="runViewFilters.search"
                  type="search"
                  class="form-control border-start-0 ps-0"
                  placeholder="Search test case..."
                  style="font-size:0.75rem;"
                />
              </div>
            </div>
            <div class="qa-tc-review-filter-field">
              <label>Type</label>
              <base-select
                size="sm"
                :model-value="runViewFilters.testType"
                :options="runViewTypeFilterOptions"
                option-label="label"
                option-value="value"
                @update:model-value="(v) => setRunViewFilter('testType', v ?? '')"
              />
            </div>
            <div class="qa-tc-review-filter-field">
              <label>Priority</label>
              <base-select
                size="sm"
                :model-value="runViewFilters.priority"
                :options="runViewPriorityFilterOptions"
                option-label="label"
                option-value="value"
                @update:model-value="(v) => setRunViewFilter('priority', v ?? '')"
              />
            </div>
            <div class="qa-tc-review-filter-field">
              <label>Scenario</label>
              <base-select
                size="sm"
                searchable
                :model-value="runViewFilters.scenario"
                :options="runViewScenarioFilterOptions"
                option-label="label"
                option-value="value"
                @update:model-value="onRunViewScenarioFilterChange"
              />
            </div>
            <div class="qa-tc-review-filter-field">
              <label>Sub-Scenario</label>
              <base-select
                size="sm"
                searchable
                :model-value="runViewFilters.subScenario"
                :options="runViewSubScenarioFilterOptions"
                option-label="label"
                option-value="value"
                @update:model-value="(v) => setRunViewFilter('subScenario', v ?? '')"
              />
            </div>
            <div class="qa-tc-review-filter-field">
              <label>Status</label>
              <base-select
                size="sm"
                :model-value="runViewFilters.status"
                :options="runViewStatusFilterOptions"
                option-label="label"
                option-value="value"
                @update:model-value="(v) => setRunViewFilter('status', v ?? '')"
              />
            </div>
            <div class="qa-tc-review-filter-field qa-tc-review-filter-field--clear">
              <label class="qa-tc-review-filter-clear-label">&nbsp;</label>
              <button type="button" class="btn btn-sm btn-outline-secondary mb-0 qa-tr-view-clear-btn" @click="clearRunViewFilters">
                Clear Filters
              </button>
            </div>
          </div>
          <div class="qa-tc-review-body">
            <div class="qa-tc-review-doc">
              <div class="qa-tc-review-content" :class="{ 'qa-tc-review-content--pdf': runViewMode === 'pdf' }">
                <div class="qa-tc-review-title-block">
                  <div class="qa-tc-review-info-grid">
                    <div>
                      <span>Status</span>
                      <strong class="qa-tr-view-result" :style="{ color: viewStatusColor(run.status) }">{{ displayRunStatus(run.status) }}</strong>
                    </div>
                    <div>
                      <span>Result</span>
                      <strong class="qa-tr-view-result" :style="{ color: viewResultColor(runViewSummaryResult) }">{{ runViewSummaryResult }}</strong>
                    </div>
                    <div>
                      <span>Test Case total</span>
                      <strong>{{ runViewFilteredCases.length }}</strong>
                    </div>
                  </div>
                </div>
                <div v-if="runViewMode === 'pdf'" class="qa-tc-pdf-wrap">
                  <table class="qa-tc-pdf-table qa-tr-view-table">
                    <colgroup>
                      <col style="width:45px" />
                      <col style="width:185px" />
                      <col style="width:43px" />
                      <col style="width:43px" />
                      <col style="width:185px" />
                      <col style="width:185px" />
                      <col style="width:185px" />
                      <col style="width:80px" />
                    </colgroup>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Test Case Name</th>
                        <th>Test Type</th>
                        <th>Priority</th>
                        <th>Test Step</th>
                        <th>Expected Result</th>
                        <th>Actual Result</th>
                        <th>Result</th>
                      </tr>
                    </thead>
                    <tbody v-if="!runViewFilteredCases.length">
                      <tr>
                        <td colspan="8" class="qa-tc-review-status">No test cases match this filter.</td>
                      </tr>
                    </tbody>
                    <tbody v-for="fnGroup in runViewGroupedCases" :key="fnGroup.function">
                      <template v-for="scGroup in fnGroup.scenarioGroups" :key="fnGroup.function + '|' + scGroup.scenario">
                        <tr class="qa-tc-pdf-section qa-tc-pdf-section--sc">
                          <td colspan="8">Scenario: {{ scGroup.scenario || "(No Scenario)" }}</td>
                        </tr>
                        <template v-for="subGroup in scGroup.subGroups" :key="subGroup.subScenario">
                          <tr class="qa-tc-pdf-section qa-tc-pdf-section--sub">
                            <td colspan="8">Sub-Scenario: {{ subGroup.subScenario || "(No Sub-Scenario)" }}</td>
                          </tr>
                          <template v-for="tc in subGroup.cases" :key="tc.key">
                            <tr class="qa-tc-pdf-case">
                              <td :rowspan="viewStepCount(tc)">{{ tc.tcLabel }}</td>
                              <td :rowspan="viewStepCount(tc)">
                                <BulletText :text="tc.title" list-class="mb-0" fallback="Untitled" />
                              </td>
                              <td class="text-center" :rowspan="viewStepCount(tc)">{{ tc.testType || tc.typeOfTestCase || "—" }}</td>
                              <td class="text-center" :rowspan="viewStepCount(tc)">{{ tc.priority || "—" }}</td>
                              <td><BulletText :text="viewStepText(tc, 0)" list-class="mb-0" fallback="—" /></td>
                              <td><BulletText :text="viewExpectedText(tc, 0)" list-class="mb-0" fallback="—" /></td>
                              <td :rowspan="viewActualIsByStep(tc) ? 1 : viewStepCount(tc)">
                                <BulletText :text="viewActualText(tc, 0)" list-class="mb-0" fallback="—" />
                                <div v-if="viewStepActualMediaPreview(tc, 0).length" class="qa-tr-view-media">
                                  <button
                                    v-for="item in viewStepActualMediaPreview(tc, 0)"
                                    :key="item.index"
                                    type="button"
                                    class="qa-tr-view-media-thumb"
                                    @click="openViewStepMedia(tc, 0, item.index)"
                                  >
                                    <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                    <img
                                      v-else
                                      :src="item.src"
                                      alt=""
                                      referrerpolicy="no-referrer"
                                      @error="onViewMediaError(tc, item.index, $event)"
                                    />
                                    <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                      <i class="fas fa-play"></i>
                                    </span>
                                    <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                                  </button>
                                </div>
                              </td>
                              <td class="text-center" :rowspan="viewStepCount(tc)">
                                <strong class="qa-tr-view-result" :style="{ color: viewResultColor(tc.runStatus) }">{{ resultDisplayLabel(tc.runStatus) }}</strong>
                              </td>
                            </tr>
                            <tr v-for="(step, idx) in viewExtraSteps(tc)" :key="`${tc.key}-s-${idx}`" class="qa-tc-pdf-case qa-tc-pdf-case--cont">
                              <td><BulletText :text="viewStepText(tc, idx + 1)" list-class="mb-0" fallback="—" /></td>
                              <td><BulletText :text="viewExpectedText(tc, idx + 1)" list-class="mb-0" fallback="—" /></td>
                              <td v-if="viewActualIsByStep(tc)">
                                <BulletText :text="viewActualText(tc, idx + 1)" list-class="mb-0" fallback="—" />
                                <div v-if="viewStepActualMediaPreview(tc, idx + 1).length" class="qa-tr-view-media">
                                  <button
                                    v-for="item in viewStepActualMediaPreview(tc, idx + 1)"
                                    :key="item.index"
                                    type="button"
                                    class="qa-tr-view-media-thumb"
                                    @click="openViewStepMedia(tc, idx + 1, item.index)"
                                  >
                                    <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                    <img
                                      v-else
                                      :src="item.src"
                                      alt=""
                                      referrerpolicy="no-referrer"
                                      @error="onViewMediaError(tc, item.index, $event)"
                                    />
                                    <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                      <i class="fas fa-play"></i>
                                    </span>
                                    <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          </template>
                        </template>
                      </template>
                    </tbody>
                  </table>
                </div>
                <template v-else>
                  <p v-if="!runViewStandardCases.length" class="qa-tc-review-status">No test cases match this filter.</p>
                  <article v-for="tc in runViewStandardCases" :key="tc.key" class="qa-tc-review-case">
                    <div class="qa-tc-review-case-top">
                      <div>
                        <p class="qa-tc-review-case-id mb-0">{{ tc.tcLabel }}</p>
                        <h6 class="qa-tc-review-case-name mb-1">{{ tc.title || "Untitled" }}</h6>
                        <div class="qa-tc-review-type-row">
                          <span class="qa-tc-review-type-item">
                            <strong class="qa-tc-review-k">Type:</strong>
                            <span class="qa-tc-review-type-val" :class="reviewTypeToneClass(tc.testType)">{{ enumLabel(tc.testType) }}</span>
                          </span>
                          <span class="qa-tc-review-type-item">
                            <strong class="qa-tc-review-k">Priority:</strong>
                            <span class="qa-tc-review-priority-val" :class="reviewPriorityToneClass(tc.priority)">{{ enumLabel(tc.priority) }}</span>
                          </span>
                        </div>
                        <div class="qa-tc-review-hierarchy">
                          <div><strong class="qa-tc-review-k">Function:</strong> {{ tc.function || "—" }}</div>
                          <div><strong class="qa-tc-review-k">Scenario:</strong> {{ tc.scenario || "—" }}</div>
                          <div><strong class="qa-tc-review-k">Sub-Scenario:</strong> {{ tc.subScenario || "—" }}</div>
                        </div>
                      </div>
                      <div class="qa-tr-view-result-head">
                        <strong class="qa-tc-review-k">Result:</strong>
                        <span
                          class="qa-tr-view-result-chip"
                          :class="[
                            viewResultChipClass(tc.runStatus),
                            tc.runStatus === 'Pass with Condition' ? 'is-pass-condition' : '',
                          ]"
                        >{{ resultDisplayLabel(tc.runStatus) }}</span>
                      </div>
                    </div>
                    <div class="qa-tc-review-fields">
                      <div>
                        <p class="qa-tc-review-label">Pre-Condition</p>
                        <div class="qa-tc-review-value">
                          <BulletText :text="tc.preconditions" list-class="mb-0" fallback="—" />
                        </div>
                      </div>
                      <div>
                        <p class="qa-tc-review-label">Test Data</p>
                        <div class="qa-tc-review-value">
                          <BulletText :text="tc.testData" list-class="mb-0" fallback="—" />
                        </div>
                      </div>
                    </div>
                    <table class="qa-tc-review-steps">
                      <colgroup>
                        <col class="qa-tc-review-col-no" />
                        <col class="qa-tc-review-col-module" />
                        <col class="qa-tc-review-col-step" />
                        <col class="qa-tc-review-col-expected" />
                        <col class="qa-tc-review-col-actual" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Step</th>
                          <th>Module</th>
                          <th>Test Step</th>
                          <th>Expected Result</th>
                          <th>Actual Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="!(tc.steps || []).length">
                          <td class="text-secondary">—</td>
                          <td class="text-secondary">—</td>
                          <td class="text-secondary">—</td>
                          <td class="text-secondary">—</td>
                          <td>
                            <BulletText :text="viewOverviewActual(tc)" list-class="mb-0" fallback="—" />
                            <div v-if="viewActualMediaPreview(tc).length" class="qa-tr-view-media mt-1">
                              <button
                                v-for="item in viewActualMediaPreview(tc)"
                                :key="item.index"
                                type="button"
                                class="qa-tr-view-media-thumb"
                                @click="openViewMedia(tc, item.index)"
                              >
                                <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                <img
                                  v-else
                                  :src="item.src"
                                  alt=""
                                  referrerpolicy="no-referrer"
                                  @error="onViewMediaError(tc, item.index, $event)"
                                />
                                <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                  <i class="fas fa-play"></i>
                                </span>
                                <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                        <tr v-for="(step, idx) in tc.steps" :key="`${tc.key}-std-${idx}`">
                          <td>{{ step.stepNo || idx + 1 }}</td>
                          <td><BulletText :text="step.platform" list-class="mb-0" fallback="—" /></td>
                          <td><BulletText :text="step.stepDetail" list-class="mb-0" fallback="—" /></td>
                          <td><BulletText :text="step.expectedResult" list-class="mb-0" fallback="—" /></td>
                          <td v-if="viewActualIsByStep(tc) || idx === 0" :rowspan="viewActualIsByStep(tc) ? 1 : tc.steps.length">
                            <BulletText :text="viewActualText(tc, idx)" list-class="mb-0" fallback="—" />
                            <div v-if="viewStepActualMediaPreview(tc, idx).length" class="qa-tr-view-media mt-1">
                              <button
                                v-for="item in viewStepActualMediaPreview(tc, idx)"
                                :key="item.index"
                                type="button"
                                class="qa-tr-view-media-thumb"
                                @click="openViewStepMedia(tc, idx, item.index)"
                              >
                                <video v-if="isVideoMediaUrl(item.src)" :src="item.src" muted preload="metadata" playsinline />
                                <img
                                  v-else
                                  :src="item.src"
                                  alt=""
                                  referrerpolicy="no-referrer"
                                  @error="onViewMediaError(tc, item.index, $event)"
                                />
                                <span v-if="isVideoMediaUrl(item.src) && !item.extra" class="qa-tr-view-media-play">
                                  <i class="fas fa-play"></i>
                                </span>
                                <span v-if="item.extra" class="qa-tr-view-media-more">+{{ item.extra }}</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </article>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div v-if="showRenumberConfirm" class="modal-overlay" style="z-index:3200;">
        <div class="modal-box" style="width:420px; max-width:92vw; padding:2rem;">
          <h6 class="mb-0 fw-semibold mb-3">Renumber Run IDs</h6>
          <p class="text-sm text-secondary mb-4">
            Renumber this run as TCR-001, TCR-002, … in the current list order.
            Original TC IDs stay as reference.
          </p>
          <div class="d-flex justify-content-end gap-2">
            <button
              type="button"
              class="btn btn-sm btn-outline-secondary mb-0"
              :disabled="renumberingRunIds"
              @click="showRenumberConfirm = false"
            >Cancel</button>
            <button
              type="button"
              class="btn btn-sm bg-gradient-dark text-white mb-0"
              :disabled="renumberingRunIds"
              @click="confirmRenumberRunIds"
            >
              <i v-if="renumberingRunIds" class="fas fa-spinner fa-spin me-1"></i>
              {{ renumberingRunIds ? "Renumbering..." : "Renumber" }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Toast -->
    <div v-if="toast.show" class="run-toast" :class="toast.type">
      <i :class="toast.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
      {{ toast.message }}
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { API_BASE_URL, fetchWithTimeout } from "@/config/apiBase";
import { testRunsApi, testRunDetailsApi, reportsApi, actualResultApi } from "@/services/backendApi";
import BaseSelect from "@/components/BaseSelect.vue";
import BulletText from "@/components/BulletText.vue";
import JiraStatusSelect from "@/components/JiraStatusSelect.vue";
import { enrichJiraLinkRows, fetchJiraIssuesByKeys } from "@/services/jiraStatusApi";

function formatDate(val) {
  if (!val) return "—";
  const s = String(val).substring(0, 10);
  const [y, m, d] = s.split("-");
  return `${d}-${m}-${y}`;
}

const MEDIA_ACCEPT = "image/*,video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov";
const MAX_VIDEO_BYTES = 150 * 1024 * 1024;
const MAX_VIDEO_MB = 150;
const TARGET_DATA_URL_CHARS = 420000;

function isVideoMediaUrl(url) {
  if (!url || typeof url !== "string") return false;
  if (url.startsWith("data:video")) return true;
  return /\.(mp4|webm|mov)(\?|$)/i.test(url.toLowerCase());
}

function compressDataUrlImage(dataUrl, maxWidth = 1280, quality = 0.82) {
  if (!dataUrl || !dataUrl.startsWith("data:")) return Promise.resolve(dataUrl);
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let w = img.width;
      let h = img.height;
      if (w > maxWidth) {
        h = Math.round(h * (maxWidth / w));
        w = maxWidth;
      }
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => reject(new Error("Failed to process image"));
    img.src = dataUrl;
  });
}

async function compressDataUrlToTarget(dataUrl, targetChars = TARGET_DATA_URL_CHARS) {
  if (!dataUrl || !dataUrl.startsWith("data:image")) return dataUrl;
  let width = 1280;
  let quality = 0.82;
  let result = await compressDataUrlImage(dataUrl, width, quality);
  for (let i = 0; i < 10 && result.length > targetChars; i++) {
    if (quality > 0.45) quality = Math.max(0.45, quality - 0.1);
    else width = Math.max(640, width - 128);
    result = await compressDataUrlImage(dataUrl, width, quality);
  }
  return result;
}

function normalizeActualResultMode(mode) {
  const m = String(mode || "overview").trim().toLowerCase();
  if (m === "step" || m === "bystep") return "byStep";
  return "overview";
}

function isByStepMode(mode) {
  const m = String(mode || "").trim().toLowerCase();
  return m === "step" || m === "bystep";
}

function canonicalizeImageUrl(url) {
  if (!url || typeof url !== "string") return url;
  if (url.startsWith("data:")) return url;
  const q = url.indexOf("?");
  if (q > 0 && url.includes(".amazonaws.com/")) return url.slice(0, q);
  return url;
}

function normalizeMediaUrls(value) {
  const list = Array.isArray(value) ? value : (value ? [value] : []);
  return list
    .map((item) => String(item || "").trim().replace(/^"+|"+$/g, ""))
    .filter((item) => /^(https?:\/\/|data:)/i.test(item));
}

function isPendingUploadImage(img) {
  return !!img && String(img).startsWith("data:");
}

function isOverviewActualRow(row) {
  const m = String(row?.actualMode || "overview").trim().toLowerCase();
  return m !== "step" && m !== "bystep";
}

function findOverviewActualRow(rows) {
  return (rows || []).find(isOverviewActualRow) || null;
}

function resolvedActualImages(row) {
  const fromList = Array.isArray(row?.actualImages) ? row.actualImages.filter(Boolean) : [];
  if (fromList.length > 0) return fromList;
  return row?.actualImage ? [row.actualImage] : [];
}

async function prepareMediaForSave(mediaList) {
  const list = (mediaList || []).map(canonicalizeImageUrl).filter(Boolean);
  if (!list.length) return [];
  return Promise.all(
    list.map(async (url) => {
      if (isVideoMediaUrl(url)) return url;
      if (url.startsWith("http://") || url.startsWith("https://")) return url;
      if (url.startsWith("data:image")) return compressDataUrlToTarget(url);
      return url;
    })
  );
}

async function prepareMediaFile(file, onProgress) {
  const type = (file.type || "").toLowerCase();
  const report = (phase, percent) => onProgress && onProgress({ phase, percent });
  if (isVideoFile(file)) {
    if (file.size > MAX_VIDEO_BYTES) {
      throw new Error(`Video must be under ${MAX_VIDEO_MB}MB (${file.name})`);
    }
    return readFileAsDataUrl(file, (percent) => report("reading", percent));
  }
  if (type.startsWith("image/")) {
    const dataUrl = await readFileAsDataUrl(file, (percent) => report("reading", percent));
    report("compressing", null);
    return compressDataUrlToTarget(dataUrl);
  }
  throw new Error(`Unsupported file type: ${file.name}`);
}

function isVideoFile(file) {
  const type = (file?.type || "").toLowerCase();
  return type.startsWith("video/") || /\.(mp4|webm|mov)$/i.test(file?.name || "");
}

async function parseApiJsonResponse(res) {
  const text = await res.text();
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!text || (!ct.includes("json") && text.trimStart().startsWith("<"))) {
    if (res.status === 413) {
      throw new Error("Upload too large. Try fewer or smaller files.");
    }
    const brief = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 120);
    throw new Error(brief || `Server error (${res.status})`);
  }
  try {
    const json = JSON.parse(text);
    if (!res.ok) throw new Error(json.error || `Request failed (${res.status})`);
    return json;
  } catch (e) {
    if (e.message && !String(e.message).includes("JSON")) throw e;
    throw new Error("Invalid server response. Please try again.");
  }
}

function readFileAsDataUrl(file, onProgress) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = () => reject(new Error("Failed to read file"));
    if (onProgress && file?.size > 0) {
      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };
    }
    reader.readAsDataURL(file);
  });
}

function postJsonWithUploadProgress(url, body, onProgress, timeoutMs = 300000) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const timer = setTimeout(() => {
      xhr.abort();
      reject(new Error("Request timed out"));
    }, timeoutMs);
    xhr.open("POST", url);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      clearTimeout(timer);
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        text: () => Promise.resolve(xhr.responseText || ""),
        headers: { get: (name) => xhr.getResponseHeader(name) },
      });
    };
    xhr.onerror = () => {
      clearTimeout(timer);
      reject(new Error("Network error"));
    };
    xhr.onabort = () => {
      clearTimeout(timer);
      reject(new Error("Request aborted"));
    };
    xhr.send(JSON.stringify(body));
  });
}

export default {
  name: "test-run-detail",
  components: { BaseSelect, BulletText, JiraStatusSelect },
  data() {
    return {
      run: {},
      loading: false,
      loadError: "",
      detailCases: [],
      renumberingRunIds: false,
      showRenumberConfirm: false,
      showRunView: false,
      runViewMode: "standard",
      runViewFilters: { search: "", testType: "", priority: "", scenario: "", subScenario: "", status: "" },
      showActualModal: false,
      modalTc: null,
      saving: false,
      loadingSteps: false,
      lightboxSrc: null,
      lightboxIsVideo: false,
      lightboxItems: [],
      lightboxIndex: 0,
      mediaAccept: MEDIA_ACCEPT,
      mediaRefreshAttempted: {},
      assignableUsers: [],
      loadingAssignable: false,
      jiraIssueForm: { title: "", description: "", assigneeAccountId: null, images: [], spaceKey: null, relatedTaskKey: null },
      jiraSpaces: [],
      jiraSpaceTasks: [],
      loadingJiraSpaces: false,
      loadingJiraSpaceTasks: false,
      activeSpaceSprint: null,
      loadingSpaceSprint: false,
      jiraIssueCreating: false,
      jiraIssueError: null,
      showJiraCreateForm: false,
      showJiraLinkForm: false,
      jiraLinkIssueKey: "",
      jiraLinkSelectedIssueKey: null,
      jiraLinkSpaceKey: null,
      jiraLinkSpaceIssues: [],
      loadingJiraLinkIssues: false,
      jiraLinkPreview: null,
      jiraLinkPreviewLoading: false,
      jiraLinkPreviewError: null,
      jiraLinkSubmitting: false,
      createdJiraIssues: [],
      jiraLinkedFromTcItems: [],
      loadingJiraLinkedFrom: false,
      modalTcJiraItems: [],
      loadingModalJira: false,
      collapsedFunctions: [],
      collapsedScenarios: [],
      collapsedSubScenarios: [],
      search: "",
      filtersPanelOpen: false,
      filters: {
        function: "",
        scenario: "",
        subScenario: "",
        status: "",
      },
      toast: { show: false, type: "success", message: "" },
      mediaUploadState: null,
      pendingMediaSlots: [],
      videoMediaLoadedKeys: {},
      actualModalSideTab: "result",
      actualResultHistory: [],
      actualResultHistoryLoading: false,
    };
  },
  async created() {
    await this.loadAll();
  },
  mounted() {
    document.documentElement.classList.add("run-detail-scroll-lock");
  },
  beforeUnmount() {
    document.documentElement.classList.remove("run-detail-scroll-lock");
    this.unbindRunViewStickyHeaders();
    this.showRunView = false;
  },
  watch: {
    "$route.params.runIndex"() {
      this.loadAll();
    },
    showRunView() {
      this.syncRunViewStickyHeaders();
    },
    runViewMode() {
      this.syncRunViewStickyHeaders();
    },
    runViewFilteredCases() {
      this.syncRunViewStickyHeaders();
    },
    "modalTc.actualResultMode"() {
      if (!this.showActualModal) return;
      this.actualModalSideTab = "result";
    },
  },
  computed: {
    ...mapState({
      user: (state) => state.user,
    }),
    currentUserId() {
      const raw = this.user?.userId;
      return raw != null ? Number(raw) : null;
    },
    runBackTarget() {
      if (String(this.$route.query?.from || "") === "qa") {
        return { name: "QA Test Run" };
      }
      return `/test-plan/${this.$route.params.id}`;
    },
    allCases() {
      return Array.isArray(this.detailCases) ? this.detailCases : [];
    },
    runViewSummaryResult() {
      const statuses = this.allCases.map((tc) => String(tc.runStatus || "").trim());
      if (!statuses.length || statuses.every((s) => !s || s === "No Run" || s === "Not Run")) return "No Run";
      if (statuses.some((s) => s === "Fail" || s === "Failed")) return "Failed";
      if (statuses.some((s) => s === "Blocked")) return "Blocked";
      if (statuses.some((s) => s === "Pass with Condition")) return "Pass with Condition";
      if (statuses.some((s) => s === "In Progress" || s === "In progress")) return "In progress";
      if (statuses.every((s) => s === "Pass")) return "Passed";
      return "In progress";
    },
    functionFilterOptions() {
      const set = new Set();
      this.allCases.forEach((tc) => set.add(tc.function || ""));
      return [...set].sort((a, b) => a.localeCompare(b));
    },
    scenarioFilterOptions() {
      const set = new Set();
      this.allCases.forEach((tc) => {
        if (this.filters.function && (tc.function || "") !== this.filters.function) return;
        set.add(tc.scenario || "");
      });
      return [...set].sort((a, b) => a.localeCompare(b));
    },
    subScenarioFilterOptions() {
      const set = new Set();
      this.allCases.forEach((tc) => {
        if (this.filters.function && (tc.function || "") !== this.filters.function) return;
        if (this.filters.scenario && (tc.scenario || "") !== this.filters.scenario) return;
        set.add(tc.subScenario || "");
      });
      return [...set].sort((a, b) => a.localeCompare(b));
    },
    statusFilterOptions() {
      return [
        "No Run",
        "Not Run",
        "In progress",
        "Pass",
        "Pass with Condition",
        "Fail",
        "Blocked",
      ];
    },
    actualResultModeOptions() {
      return [
        { label: "Result by Step", value: "byStep" },
        { label: "Overview", value: "overview" },
      ];
    },
    runStatusOptions() {
      return [
        { label: "No Run", value: "No Run" },
        { label: "Not Run", value: "Not Run" },
        { label: "Pass", value: "Pass" },
        { label: "Pass with Condition", value: "Pass with Condition" },
        { label: "In progress", value: "In progress" },
        { label: "Failed", value: "Fail" },
        { label: "Blocked", value: "Blocked" },
      ];
    },
    functionFilterSelectOptions() {
      return [
        { label: "All Function", value: "" },
        ...this.functionFilterOptions.map((fn) => ({
          label: fn || "(No Function)",
          value: fn,
        })),
      ];
    },
    scenarioFilterSelectOptions() {
      return [
        { label: "All Scenario", value: "" },
        ...this.scenarioFilterOptions.map((sc) => ({
          label: sc || "(No Scenario)",
          value: sc,
        })),
      ];
    },
    subScenarioFilterSelectOptions() {
      return [
        { label: "All Sub-Scenario", value: "" },
        ...this.subScenarioFilterOptions.map((sub) => ({
          label: sub || "(No Sub-Scenario)",
          value: sub,
        })),
      ];
    },
    statusFilterSelectOptions() {
      return [
        { label: "All Status", value: "" },
        ...this.statusFilterOptions.map((st) => ({
          label: this.resultDisplayLabel(st),
          value: st,
        })),
      ];
    },
    hasActiveRunFilters() {
      return !!(
        String(this.search || "").trim()
        || this.filters.function
        || this.filters.scenario
        || this.filters.subScenario
        || this.filters.status
      );
    },
    emptyCasesMessage() {
      if (!this.detailCases.length) return "No test cases in this run.";
      return "No test cases match the selected filters or search.";
    },
    filteredCases() {
      const q = this.search.trim().toLowerCase();
      return this.allCases.filter((tc) => {
        if (this.filters.function && (tc.function || "") !== this.filters.function) return false;
        if (this.filters.scenario && (tc.scenario || "") !== this.filters.scenario) return false;
        if (this.filters.subScenario && (tc.subScenario || "") !== this.filters.subScenario) return false;
        if (this.filters.status) {
          if (!this.runStatusMatchesFilter(tc, this.filters.status)) return false;
        }
        if (q && !this.caseMatchesSearch(tc, q)) return false;
        return true;
      });
    },
    runViewFilteredCases() {
      const f = this.runViewFilters || {};
      const scenario = String(f.scenario || "");
      const subScenario = String(f.subScenario || "");
      const status = String(f.status || "").trim();
      const testType = String(f.testType || "").trim().toLowerCase();
      const priority = String(f.priority || "").trim().toLowerCase();
      const q = String(f.search || "").trim().toLowerCase();
      return this.allCases.filter((tc) => {
        if (scenario && String(tc.scenario || "") !== scenario) return false;
        if (subScenario && String(tc.subScenario || "") !== subScenario) return false;
        if (status && !this.runStatusMatchesFilter(tc, status)) return false;
        if (testType) {
          const typeVal = String(tc.testType || tc.typeOfTestCase || "").trim().toLowerCase();
          if (typeVal !== testType) return false;
        }
        if (priority && String(tc.priority || "").trim().toLowerCase() !== priority) return false;
        if (q && !this.caseMatchesSearch(tc, q)) return false;
        return true;
      });
    },
    runViewTypeFilterOptions() {
      return [
        { label: "All Type", value: "" },
        { label: "Positive", value: "Positive" },
        { label: "Negative", value: "Negative" },
      ];
    },
    runViewPriorityFilterOptions() {
      return [
        { label: "All Priority", value: "" },
        { label: "High", value: "high" },
        { label: "Medium", value: "medium" },
        { label: "Low", value: "low" },
      ];
    },
    runViewScenarioFilterOptions() {
      const values = [...new Set(
        this.allCases.map((tc) => String(tc.scenario || "").trim()).filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));
      return [{ label: "All Scenario", value: "" }, ...values.map((value) => ({ label: value, value }))];
    },
    runViewSubScenarioFilterOptions() {
      const scenario = String(this.runViewFilters.scenario || "");
      const values = [...new Set(
        this.allCases
          .filter((tc) => !scenario || String(tc.scenario || "") === scenario)
          .map((tc) => String(tc.subScenario || "").trim())
          .filter(Boolean)
      )].sort((a, b) => a.localeCompare(b));
      return [{ label: "All Sub-Scenario", value: "" }, ...values.map((value) => ({ label: value, value }))];
    },
    runViewStatusFilterOptions() {
      return [
        { label: "All Status", value: "" },
        ...this.statusFilterOptions.map((st) => ({
          label: this.resultDisplayLabel(st),
          value: st,
        })),
      ];
    },
    runViewGroupedCases() {
      const result = [];
      const fnMap = new Map();
      for (const tc of this.runViewFilteredCases) {
        const fn = tc.function || "";
        const sc = tc.scenario || "";
        const sub = tc.subScenario || "";
        if (!fnMap.has(fn)) {
          const fnGroup = { function: fn, scenarioGroups: [], scenarioMap: new Map() };
          fnMap.set(fn, fnGroup);
          result.push(fnGroup);
        }
        const fnGroup = fnMap.get(fn);
        if (!fnGroup.scenarioMap.has(sc)) {
          const scGroup = { scenario: sc, subGroups: [], subMap: new Map() };
          fnGroup.scenarioMap.set(sc, scGroup);
          fnGroup.scenarioGroups.push(scGroup);
        }
        const scGroup = fnGroup.scenarioMap.get(sc);
        if (!scGroup.subMap.has(sub)) {
          const subGroup = { subScenario: sub, cases: [] };
          scGroup.subMap.set(sub, subGroup);
          scGroup.subGroups.push(subGroup);
        }
        scGroup.subMap.get(sub).cases.push(tc);
      }
      return this.sortRunCaseGroups(result, true);
    },
    runViewStandardCases() {
      return [...(this.runViewFilteredCases || [])].sort((a, b) => this.sortRunCasesTopToBottom(a, b));
    },
    groupedFilteredCases() {
      const result = [];
      const fnMap = new Map();
      for (const tc of this.filteredCases) {
        const fn = tc.function || "";
        const sc = tc.scenario || "";
        const sub = tc.subScenario || "";
        if (!fnMap.has(fn)) {
          const fnGroup = { function: fn, scenarioGroups: [], scenarioMap: new Map() };
          fnMap.set(fn, fnGroup);
          result.push(fnGroup);
        }
        const fnGroup = fnMap.get(fn);
        if (!fnGroup.scenarioMap.has(sc)) {
          const scGroup = { scenario: sc, subGroups: [], subMap: new Map() };
          fnGroup.scenarioMap.set(sc, scGroup);
          fnGroup.scenarioGroups.push(scGroup);
        }
        const scGroup = fnGroup.scenarioMap.get(sc);
        if (!scGroup.subMap.has(sub)) {
          const subGroup = { subScenario: sub, cases: [] };
          scGroup.subMap.set(sub, subGroup);
          scGroup.subGroups.push(subGroup);
        }
        scGroup.subMap.get(sub).cases.push(tc);
      }
      return this.sortRunCaseGroups(result);
    },
    jiraProjectKeyForIssue() {
      const k = this.modalTcJiraItems?.[0]?.key;
      if (!k || typeof k !== "string") return "";
      const i = k.indexOf("-");
      return i > 0 ? k.slice(0, i).toUpperCase() : "";
    },
    jiraCreateMode() {
      return (this.modalTcJiraItems?.length > 0) ? "legacy" : "standalone";
    },
    jiraStandaloneProjectKey() {
      return this.jiraIssueForm.spaceKey ? String(this.jiraIssueForm.spaceKey).toUpperCase() : "";
    },
    jiraEffectiveProjectKey() {
      return this.jiraCreateMode === "legacy"
        ? this.jiraProjectKeyForIssue
        : this.jiraStandaloneProjectKey;
    },
    canSubmitJiraIssue() {
      if (!this.jiraIssueForm.title.trim() || !this.jiraIssueForm.description.trim()) return false;
      if (this.jiraCreateMode === "standalone") {
        return !!this.jiraIssueForm.spaceKey;
      }
      return this.modalTcJiraItems.length > 0;
    },
    canSubmitJiraLink() {
      if (!this.resolvedJiraLinkIssueKey) return false;
      if (this.jiraCreateMode === "legacy") {
        return this.modalTcJiraItems.length > 0;
      }
      return true;
    },
    resolvedJiraLinkIssueKey() {
      if (this.jiraLinkSelectedIssueKey) {
        return String(this.jiraLinkSelectedIssueKey).trim().toUpperCase();
      }
      const key = String(this.jiraLinkIssueKey || "").trim().toUpperCase();
      return /^[A-Z][A-Z0-9_]*-\d+$/.test(key) ? key : "";
    },
    jiraPanelIssueList() {
      const seen = new Set();
      const merged = [];
      const add = (item) => {
        const k = String(item?.key || "").trim().toUpperCase();
        if (!k || seen.has(k)) return;
        seen.add(k);
        merged.push(item);
      };
      (this.createdJiraIssues || []).forEach((item) => add(item));
      (this.jiraLinkedFromTcItems || []).forEach((item) => add({
        ...item,
        issueType: item.issueType || null,
        url: item.url || this.jiraBrowseUrl(item.key),
      }));
      return merged;
    },
    jiraPanelIssueListLoading() {
      return this.loadingJiraLinkedFrom && this.jiraCreateMode === "legacy";
    },
    hasModalLinkedJira() {
      if ((this.createdJiraIssues || []).length > 0) return true;
      if ((this.jiraPanelIssueList || []).length > 0) return true;
      if ((this.modalTcJiraItems || []).length > 0) return true;
      return this.parseTcJiraLinks(this.modalTc?.jiraLink).length > 0;
    },
    showJiraIssuePanel() {
      if (!this.modalTc) return false;
      if (this.isFailedOrBlocked(this.modalTc.runStatus)) return true;
      return this.hasModalLinkedJira;
    },
    isMediaUploadBusy() {
      return !!this.mediaUploadState || this.pendingMediaSlots.length > 0;
    },
    overviewPendingSlots() {
      return this.pendingMediaSlots.filter((s) => s.scope === "overview");
    },
    mediaUploadProgressLabel() {
      const s = this.mediaUploadState;
      if (!s) return "";
      const phaseLabels = {
        reading: "Reading",
        compressing: "Compressing",
        uploading: "Uploading",
      };
      let text = phaseLabels[s.phase] || "Processing";
      if (s.fileName) text += `: ${s.fileName}`;
      if (s.fileTotal > 1) text += ` (${s.fileIndex}/${s.fileTotal})`;
      return text;
    },
    mediaUploadPercent() {
      const p = this.mediaUploadState?.percent;
      return p == null ? null : p;
    },
    mediaUploadBarStyle() {
      const p = this.mediaUploadPercent;
      return p == null ? {} : { width: `${p}%` };
    },
    savingProgressLabel() {
      const s = this.mediaUploadState;
      if (s?.scope === "save") {
        if (s.phase === "uploading") return "Uploading to server…";
        if (s.phase === "compressing") return "Preparing media…";
      }
      return "Saving…";
    },
  },
  methods: {
    isUncategorizedGroupLabel(value) {
      const label = String(value || "").trim().toLowerCase();
      return !label || label === "uncategorized" || label === "(no function)" || label === "(no scenario)" || label === "(no sub-scenario)";
    },
    compareRunGroupLabel(a, b) {
      const aLast = this.isUncategorizedGroupLabel(a);
      const bLast = this.isUncategorizedGroupLabel(b);
      if (aLast !== bLast) return aLast ? 1 : -1;
      return String(a || "").localeCompare(String(b || ""), undefined, {
        sensitivity: "base",
        numeric: true,
      });
    },
    runCaseSortValue(tc) {
      const n = Number(tc?.runCaseNumber);
      if (Number.isFinite(n) && n > 0) return n;
      const fromLabel = String(tc?.tcLabel || "").match(/tcr-(\d+)/i);
      if (fromLabel) return Number(fromLabel[1]);
      const tcNo = Number(tc?.testCaseNumber);
      if (Number.isFinite(tcNo) && tcNo > 0) return tcNo + 100000;
      return Number.MAX_SAFE_INTEGER;
    },
    sortRunCasesTopToBottom(a, b) {
      const runA = this.runCaseSortValue(a);
      const runB = this.runCaseSortValue(b);
      if (runA !== runB) return runA - runB;
      const idA = Number(a.testRunDetailId || a.key) || 0;
      const idB = Number(b.testRunDetailId || b.key) || 0;
      return idA - idB;
    },
    minRunCaseSortValue(group) {
      const cases = [];
      (group?.scenarioGroups || []).forEach((sc) => {
        (sc.subGroups || []).forEach((sub) => {
          (sub.cases || []).forEach((tc) => cases.push(tc));
        });
      });
      (group?.subGroups || []).forEach((sub) => {
        (sub.cases || []).forEach((tc) => cases.push(tc));
      });
      (group?.cases || []).forEach((tc) => cases.push(tc));
      if (!cases.length) return Number.MAX_SAFE_INTEGER;
      return Math.min(...cases.map((tc) => this.runCaseSortValue(tc)));
    },
    sortRunCaseGroups(groups, byRunId = false) {
      const list = Array.isArray(groups) ? groups : [];
      const compareGroups = (a, b, labelKey) => {
        if (byRunId) return this.minRunCaseSortValue(a) - this.minRunCaseSortValue(b);
        return this.compareRunGroupLabel(a[labelKey], b[labelKey]);
      };
      list.sort((a, b) => compareGroups(a, b, "function"));
      list.forEach((fnGroup) => {
        (fnGroup.scenarioGroups || []).sort((a, b) => compareGroups(a, b, "scenario"));
        (fnGroup.scenarioGroups || []).forEach((scGroup) => {
          (scGroup.subGroups || []).sort((a, b) => compareGroups(a, b, "subScenario"));
          (scGroup.subGroups || []).forEach((subGroup) => {
            (subGroup.cases || []).sort((a, b) => this.sortRunCasesTopToBottom(a, b));
          });
        });
      });
      return list;
    },
    caseMatchesSearch(tc, q) {
      const text = [
        tc.tcLabel,
        tc.refTcLabel,
        tc.title,
        tc.description,
        tc.function,
        tc.scenario,
        tc.subScenario,
        tc.testType,
        tc.typeOfTestCase,
        tc.priority,
        tc.runStatus,
        this.resultDisplayLabel(tc.runStatus),
        tc.testCaseNumber,
        tc.runCaseNumber,
      ]
        .map((v) => String(v || "").toLowerCase())
        .join(" ");
      return text.includes(q);
    },
    formatDate,
    padCaseNo(n) {
      return String(n ?? 0).padStart(3, "0");
    },
    formatLibraryTcLabel(d) {
      return `TC-${this.padCaseNo(d?.testCaseNumber || d?.testCaseId)}`;
    },
    formatRunCaseLabel(d) {
      if (d?.runCaseNumber) return `TCR-${this.padCaseNo(d.runCaseNumber)}`;
      return this.formatLibraryTcLabel(d);
    },
    formatRunRefLabel(d) {
      if (!d?.runCaseNumber) return "";
      return this.formatLibraryTcLabel(d);
    },
    openRenumberConfirm() {
      if (this.loading || this.renumberingRunIds || !this.detailCases.length) return;
      this.showRenumberConfirm = true;
    },
    async confirmRenumberRunIds() {
      const runId = this.run?.id || this.$route.params.id;
      if (!runId || this.renumberingRunIds) return;
      this.renumberingRunIds = true;
      try {
        await testRunDetailsApi.renumberRunIds(runId);
        await this.loadAll();
        this.showRenumberConfirm = false;
        this.showToast("success", "Run IDs updated to TCR-001, TCR-002, …");
      } catch (e) {
        console.error("renumber run ids error:", e);
        this.showToast("error", e.message || "Failed to renumber run IDs.");
      } finally {
        this.renumberingRunIds = false;
      }
    },
    stepMediaKey(step, si) {
      return String(step?.stepNo ?? si);
    },
    clearMediaUpload() {
      this.mediaUploadState = null;
    },
    stepPendingSlots(stepKey) {
      return this.pendingMediaSlots.filter(
        (s) => s.scope === "step" && String(s.stepKey) === String(stepKey),
      );
    },
    pendingSlotLabel(slot) {
      const phaseLabels = {
        reading: slot.isVideo ? "Reading video" : "Reading image",
        compressing: "Compressing",
        uploading: "Uploading",
      };
      let text = phaseLabels[slot.phase] || "Processing";
      if (slot.fileTotal > 1) text += ` (${slot.fileIndex}/${slot.fileTotal})`;
      return text;
    },
    pendingSlotBarStyle(slot) {
      return slot.percent == null ? {} : { width: `${slot.percent}%` };
    },
    onVideoMediaLoaded(scope, idx) {
      this.videoMediaLoadedKeys[`${scope}-${idx}`] = true;
    },
    isVideoMediaLoaded(scope, idx) {
      return !!this.videoMediaLoadedKeys[`${scope}-${idx}`];
    },
    async processMediaFiles(files, { scope, stepKey, onEach }) {
      const list = Array.from(files || []);
      if (!list.length) return;
      for (let i = 0; i < list.length; i++) {
        const file = list[i];
        const id = `pending-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`;
        const slot = {
          id,
          scope,
          stepKey: stepKey ?? null,
          fileName: file.name,
          isVideo: isVideoFile(file),
          fileIndex: i + 1,
          fileTotal: list.length,
          percent: 0,
          phase: "reading",
        };
        this.pendingMediaSlots.push(slot);
        this.mediaUploadState = { ...slot };
        try {
          const dataUrl = await prepareMediaFile(file, ({ phase, percent }) => {
            slot.phase = phase;
            slot.percent = percent == null ? null : percent;
            this.mediaUploadState = { ...slot };
          });
          await onEach(dataUrl);
        } catch (e) {
          this.showToast("error", `Upload failed: ${e.message}`);
        } finally {
          this.pendingMediaSlots = this.pendingMediaSlots.filter((s) => s.id !== id);
        }
      }
      this.clearMediaUpload();
    },
    normalizeRunStatus(status, hasActualResult = false) {
      const s = status ? String(status).trim() : "";
      if (!s || (s === "Not Run" && !hasActualResult)) return "No Run";
      if (s === "Failed") return "Fail";
      if (s === "In Progress") return "In progress";
      return s;
    },
    runStatusMatchesFilter(tc, wanted) {
      const target = String(wanted || "").trim();
      if (!target) return true;
      const status = this.normalizeRunStatus(tc.runStatus, !!tc.hasActualResult);
      const raw = String(tc.runStatus || "").trim();
      return (
        status === target
        || raw === target
        || this.resultDisplayLabel(status) === target
        || this.resultDisplayLabel(status) === this.resultDisplayLabel(target)
        || status.toLowerCase() === target.toLowerCase()
      );
    },
    formatAssignedName(rawName) {
      const name = String(rawName || "").trim();
      if (!name) return "—";
      const nicknameMatch = name.match(/\(([^)]+)\)/);
      const firstName = name.replace(/\([^)]*\)/g, " ").trim().split(/\s+/)[0] || "";
      if (nicknameMatch && nicknameMatch[1]) {
        return `${firstName} (${nicknameMatch[1].trim()})`;
      }
      return firstName;
    },
    caseExecStatus(runStatus) {
      const raw = String(runStatus || "").trim();
      if (["Pass", "Fail", "Failed", "Pass with Condition", "Blocked"].includes(raw)) return "Done";
      if (raw === "In Progress" || raw === "In progress") return "In Progress";
      return "No Run";
    },
    caseExecStatusPill(runStatus) {
      const status = this.caseExecStatus(runStatus);
      return this.statusPillClass(status === "No Run" ? "Not Started" : status);
    },
    statusPillClass(status) {
      const map = {
        Done: "pill-done",
        "In Progress": "pill-inprogress",
        "Not Started": "pill-inactive",
        Failed: "pill-failed",
        "Done without Deploy": "pill-done-without-deploy",
        Deployed: "pill-deployed",
      };
      return map[status] || "pill-inactive";
    },
    displayRunStatus(status) {
      const raw = String(status || "").trim();
      return raw === "Not Run" ? "No Run" : (raw || "—");
    },
    viewStatusColor(status) {
      const map = {
        "Not Started": "#000033",
        "Not Run": "#000033",
        "No Run": "#666699",
        "In Progress": "#483d8b",
        "In progress": "#483d8b",
        Done: "#2eb85c",
        Failed: "#e74c3c",
        "Done without Deploy": "#4d7c4d",
        Deployed: "#336633",
      };
      return map[String(status || "").trim()] || "#000033";
    },
    viewResultColor(status) {
      const map = {
        "No Run": "#666699",
        "Not Run": "#000033",
        Fail: "#FF0000",
        Failed: "#FF0000",
        Pass: "#006400",
        Passed: "#006400",
        "Pass with Condition": "#006400",
        Blocked: "#fb6340",
        "In progress": "#000080",
        "In Progress": "#000080",
      };
      return map[String(status || "").trim()] || "#666699";
    },
    viewResultChipClass(status) {
      const map = {
        "No Run": "is-no-run",
        "Not Run": "is-not-run",
        Fail: "is-fail",
        Failed: "is-fail",
        Pass: "is-pass",
        Passed: "is-pass",
        "Pass with Condition": "is-pass",
        Blocked: "is-blocked",
        "In progress": "is-in-progress",
        "In Progress": "is-in-progress",
      };
      return map[String(status || "").trim()] || "is-no-run";
    },
    resultPillClass(result) {
      const map = {
        Pass: "pill-done",
        Passed: "pill-done",
        "Pass with Condition": "pill-done",
        Fail: "pill-failed",
        Failed: "pill-failed",
        Blocked: "pill-block",
        "No Run": "pill-inactive",
        "Not Run": "pill-inactive",
        "In Progress": "pill-inprogress",
        "In progress": "pill-inprogress",
      };
      return map[String(result || "").trim()] || "pill-inactive";
    },
    viewStepCount(tc) {
      const n = Array.isArray(tc?.steps) ? tc.steps.length : 0;
      return Math.max(n, 1);
    },
    viewExtraSteps(tc) {
      const steps = Array.isArray(tc?.steps) ? tc.steps : [];
      return steps.slice(1);
    },
    viewStepText(tc, idx) {
      const step = (tc?.steps || [])[idx];
      return step?.stepDetail || "";
    },
    viewExpectedText(tc, idx) {
      const step = (tc?.steps || [])[idx];
      return step?.expectedResult || "";
    },
    viewActualIsByStep(tc) {
      return isByStepMode(tc?.actualResultMode);
    },
    viewActualText(tc, idx) {
      if (this.viewActualIsByStep(tc)) {
        const step = (tc?.steps || [])[idx];
        const text = String(step?.actualResult || "").trim();
        return text || "—";
      }
      const text = String(tc?.actualResult || "").trim();
      return text || "—";
    },
    viewOverviewActual(tc) {
      const text = String(tc?.actualResult || "").trim();
      if (text) return text;
      const fromSteps = (tc?.steps || []).map((s) => String(s?.actualResult || "").trim()).filter(Boolean);
      return fromSteps.join("\n");
    },
    viewOverviewImages(tc) {
      const overview = normalizeMediaUrls(tc?.actualResultImages);
      if (overview.length) return overview;
      return (tc?.steps || []).flatMap((s) => normalizeMediaUrls(s.actualImages));
    },
    viewActualMediaPreview(tc) {
      return this.previewMediaItems(this.viewOverviewImages(tc));
    },
    viewStepActualImages(tc, idx) {
      if (this.viewActualIsByStep(tc)) {
        return normalizeMediaUrls((tc?.steps || [])[idx]?.actualImages);
      }
      return this.viewOverviewImages(tc);
    },
    viewStepActualMediaPreview(tc, idx) {
      return this.previewMediaItems(this.viewStepActualImages(tc, idx));
    },
    previewMediaItems(all) {
      const items = Array.isArray(all) ? all.filter(Boolean) : [];
      if (!items.length) return [];
      if (items.length <= 3) {
        return items.map((src, index) => ({ src, index, extra: 0 }));
      }
      return [
        { src: items[0], index: 0, extra: 0 },
        { src: items[1], index: 1, extra: 0 },
        { src: items[2], index: 2, extra: items.length - 2 },
      ];
    },
    openViewStepMedia(tc, idx, index) {
      const items = this.viewStepActualImages(tc, idx);
      this.lightboxItems = items;
      this.lightboxIndex = Number.isFinite(index) ? index : 0;
      this.syncLightbox();
    },
    openViewMedia(tc, index) {
      const items = this.viewOverviewImages(tc);
      this.lightboxItems = items;
      this.lightboxIndex = Number.isFinite(index) ? index : 0;
      this.syncLightbox();
    },
    syncLightbox() {
      const src = this.lightboxItems[this.lightboxIndex] || null;
      this.lightboxSrc = src;
      this.lightboxIsVideo = isVideoMediaUrl(src);
    },
    lightboxPrev() {
      if (this.lightboxItems.length < 2) return;
      this.lightboxIndex = (this.lightboxIndex - 1 + this.lightboxItems.length) % this.lightboxItems.length;
      this.syncLightbox();
    },
    lightboxNext() {
      if (this.lightboxItems.length < 2) return;
      this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxItems.length;
      this.syncLightbox();
    },
    setRunViewFilter(key, value) {
      this.runViewFilters = { ...this.runViewFilters, [key]: value ?? "" };
    },
    onRunViewScenarioFilterChange(value) {
      this.runViewFilters = { ...this.runViewFilters, scenario: value ?? "", subScenario: "" };
    },
    clearRunViewFilters() {
      this.runViewFilters = { search: "", testType: "", priority: "", scenario: "", subScenario: "", status: "" };
    },
    async openRunView() {
      this.clearRunViewFilters();
      this.runViewMode = "standard";
      this.showRunView = true;
      const runId = this.$route.params.runIndex;
      if (runId) {
        try {
          const details = await testRunDetailsApi.getByRun(runId);
          this.detailCases = this.groupDetails(details);
        } catch (e) {
          console.warn("openRunView refresh failed:", e);
        }
      }
      this.syncRunViewStickyHeaders();
    },
    async reloadViewCaseMedia(tc) {
      if (!tc?.testRunDetailId || tc._viewMediaRefreshAttempted) return;
      tc._viewMediaRefreshAttempted = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/actual-result?testRunDetailId=${tc.testRunDetailId}`);
        if (!res.ok) return;
        const actualData = await res.json();
        const overviewRow = findOverviewActualRow(actualData) || actualData[0] || null;
        const overviewImages = normalizeMediaUrls(resolvedActualImages(overviewRow));
        const original = this.allCases.find((c) => c.key === tc.key) || tc;
        original.actualResultImages = overviewImages;
        tc.actualResultImages = overviewImages;
        (original.steps || []).forEach((step) => {
          const saved = (actualData || []).find((row) => Number(row.testStepNo) === Number(step.stepNo));
          step.actualImages = normalizeMediaUrls(saved ? resolvedActualImages(saved) : []);
        });
      } catch (e) {
        console.warn("reloadViewCaseMedia failed:", e);
      }
    },
    async onViewMediaError(tc, index, event) {
      const el = event?.target;
      if (el?.dataset?.retried === "1") return;
      if (el) el.dataset.retried = "1";
      await this.reloadViewCaseMedia(tc);
      const next = this.viewOverviewImages(tc)[index];
      if (el && next && el.src !== next) el.src = next;
    },
    closeRunView() {
      this.unbindRunViewStickyHeaders();
      this.closeLightbox();
      this.showRunView = false;
    },
    unbindRunViewStickyHeaders() {
      this._runViewStickyObserver?.disconnect();
      this._runViewStickyObserver = null;
    },
    syncRunViewStickyHeaders() {
      this.unbindRunViewStickyHeaders();
      if (!this.showRunView || this.runViewMode !== "pdf") return;
      this.$nextTick(() => {
        const table = document.querySelector(".qa-tr-view-overlay .qa-tc-pdf-table");
        if (!table) return;
        const update = () => {
          const thead = table.querySelector("thead");
          const sc = table.querySelector(".qa-tc-pdf-section--sc td");
          const thH = thead ? Math.ceil(thead.getBoundingClientRect().height) : 40;
          const scH = sc ? Math.ceil(sc.getBoundingClientRect().height) : 28;
          table.style.setProperty("--qa-tc-pdf-th", `${thH}px`);
          table.style.setProperty("--qa-tc-pdf-sc", `${scH}px`);
        };
        update();
        if (typeof ResizeObserver === "undefined") return;
        this._runViewStickyObserver = new ResizeObserver(update);
        this._runViewStickyObserver.observe(table);
        const thead = table.querySelector("thead");
        if (thead) this._runViewStickyObserver.observe(thead);
      });
    },
    async generatePDF() {
      const runId = this.$route.params.runIndex;
      if (!runId) return;
      try {
        const { blob, fileName } = await reportsApi.downloadTestRunPdf(runId);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName || `TestCase_${this.run.name || "Report"}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.showToast("error", `Export failed: ${e.message}`);
      }
    },
    showToast(type, message) {
      this.toast = { show: true, type, message };
      setTimeout(() => { this.toast.show = false; }, 5000);
    },
    async loadAll() {
      const runId = this.$route.params.runIndex;
      if (!runId) {
        this.loading = false;
        this.loadError = "Test run not found.";
        this.run = {};
        this.detailCases = [];
        return;
      }
      this.loading = true;
      this.loadError = "";
      try {
        const [run, details] = await Promise.all([
          testRunsApi.getById(runId),
          testRunDetailsApi.getByRun(runId),
        ]);
        this.run = run || {};
        this.detailCases = this.groupDetails(details);
        this.search = "";
        this.filters = { function: "", scenario: "", subScenario: "", status: "" };
      } catch (e) {
        console.error("loadAll error:", e);
        this.loadError = e.message || "Failed to load test run.";
        this.run = {};
        this.detailCases = [];
        this.showToast("error", this.loadError);
      } finally {
        this.loading = false;
      }
    },
    groupDetails(details) {
      const rows = Array.isArray(details) ? details : [];
      // Group by test_case_id (distinct test cases), collect steps per case
      const tcMap = {};
      rows.forEach((d) => {
        const key = d.testCaseId ? String(d.testCaseId) : `${d.scenario}|${d.subScenario}|${String(d.title).trim()}`;
        if (!tcMap[key]) {
          tcMap[key] = {
            key,
            testRunDetailId: d.testRunDetailId,
            testRunId: d.testRunId,
            testCaseId: d.testCaseId,
            testCaseNumber: d.testCaseNumber,
            runCaseNumber: d.runCaseNumber,
            tcLabel: this.formatRunCaseLabel(d),
            refTcLabel: this.formatRunRefLabel(d),
            function: d.function || "",
            scenario: d.scenario || "",
            subScenario: d.subScenario || "",
            title: d.title ? String(d.title).trim() : "",
            description: d.description || "",
            testType: d.testType ? String(d.testType).trim() : "",
            typeOfTestCase: d.typeOfTestCase ? String(d.typeOfTestCase).trim() : "",
            priority: d.priority || "",
            executionType: d.executionType || "",
            preconditions: d.preconditions || "",
            testData: d.testData || "",
            jiraLink: d.jiraLink || null,
            runStatus: this.normalizeRunStatus(d.testCaseStatus, !!d.hasActualResult),
            hasActualResult: !!d.hasActualResult,
            actualResultMode: d.actualMode || "overview",
            actualResult: d.overviewActualDetail || "",
            actualResultImages: normalizeMediaUrls(d.overviewActualImages),
            steps: [],
          };
        }
        if (d.overviewActualDetail && !tcMap[key].actualResult) {
          tcMap[key].actualResult = d.overviewActualDetail;
        }
        if (!tcMap[key].actualResultImages.length) {
          const overviewImages = normalizeMediaUrls(d.overviewActualImages);
          if (overviewImages.length) tcMap[key].actualResultImages = overviewImages;
        }
        if (d.actualMode) tcMap[key].actualResultMode = d.actualMode;
        if (d.jiraLink) tcMap[key].jiraLink = d.jiraLink;
        if (d.stepNo != null) {
          tcMap[key].steps.push({
            stepNo: d.stepNo,
            platform: d.platform ? String(d.platform).trim() : "",
            stepDetail: d.stepDetail || "",
            expectedResult: d.expectedResult || "",
            actualResult: d.stepActualDetail || "",
            actualImages: normalizeMediaUrls(d.stepActualImages),
          });
        }
      });

      // Sort steps by step_no
      Object.values(tcMap).forEach((tc) => {
        tc.steps.sort((a, b) => (a.stepNo || 0) - (b.stepNo || 0));
      });

      return Object.values(tcMap);
    },
    countStatus(status) {
      return this.allCases.filter((tc) => tc.runStatus === status).length;
    },
    isFailedOrBlocked(status) {
      const s = String(status || "").trim();
      return s === "Fail" || s === "Failed" || s === "Blocked";
    },
    countFailedAndBlocked() {
      return this.allCases.filter((tc) => this.isFailedOrBlocked(tc.runStatus)).length;
    },
    async openActualModal(tc) {
      this.clearMediaUpload();
      this.pendingMediaSlots = [];
      this.videoMediaLoadedKeys = {};
      const seededSteps = Array.isArray(tc.steps)
        ? tc.steps.map((s) => ({
            ...s,
            actualImages: Array.isArray(s.actualImages) ? [...s.actualImages] : [],
          }))
        : [];
      this.modalTc = {
        ...tc,
        jiraLink: tc.jiraLink || null,
        steps: seededSteps,
        actualResult: tc.actualResult || "",
        actualResultImages: Array.isArray(tc.actualResultImages) ? [...tc.actualResultImages] : [],
        actualResultMode: tc.actualResultMode || "overview",
      };
      this.modalTcJiraItems = [];
      this.loadingModalJira = !!tc.testCaseId;
      this.jiraIssueForm = { title: "", description: "", assigneeAccountId: null, images: [], spaceKey: null, relatedTaskKey: null };
      this.jiraSpaces = [];
      this.jiraSpaceTasks = [];
      this.activeSpaceSprint = null;
      this.loadingSpaceSprint = false;
      this.assignableUsers = [];
      this.jiraIssueError = null;
      this.showJiraCreateForm = false;
      this.showJiraLinkForm = false;
      this.jiraLinkIssueKey = "";
      this.jiraLinkSelectedIssueKey = null;
      this.jiraLinkSpaceKey = null;
      this.jiraLinkSpaceIssues = [];
      this.jiraLinkPreview = null;
      this.jiraLinkPreviewError = null;
      this.createdJiraIssues = [];
      this.jiraLinkedFromTcItems = [];
      this.loadingJiraLinkedFrom = false;
      this.actualModalSideTab = "result";
      this.actualResultHistory = [];
      this.actualResultHistoryLoading = false;
      this.showActualModal = true;
      this.mediaRefreshAttempted = {};
      this.loadingSteps = seededSteps.length === 0;
      this.loadModalTcJira(tc);
      this.loadJiraSpaces();
      this.loadAssignableUsers();
      if (!tc.testRunDetailId) {
        this.loadingSteps = false;
        return;
      }
      this.loadActualResultHistory(tc.testRunDetailId);
      this.loadCreatedJiraIssues(tc.testRunDetailId);
      this.refreshModalActual(tc);
    },
    async refreshModalActual(tc) {
      if (!tc?.testRunDetailId || !this.modalTc) {
        this.loadingSteps = false;
        return;
      }
      try {
        if (!(this.modalTc.steps || []).length) {
          const runId = tc.testRunId || this.$route.params.runIndex;
          const detailRows = await testRunDetailsApi.getByRun(runId);
          if (!this.modalTc || Number(this.modalTc.testRunDetailId) !== Number(tc.testRunDetailId)) return;
          this.modalTc.steps = (detailRows || [])
            .filter((d) => Number(d.testRunDetailId) === Number(tc.testRunDetailId) && d.stepNo != null)
            .sort((a, b) => (a.stepNo || 0) - (b.stepNo || 0))
            .map((s, i) => ({
              stepNo: s.stepNo || i + 1,
              platform: s.platform ? String(s.platform).trim() : "",
              stepDetail: s.stepDetail || "",
              expectedResult: s.expectedResult || "",
              actualResult: s.stepActualDetail || "",
              actualImages: Array.isArray(s.stepActualImages) ? s.stepActualImages.filter(Boolean) : [],
            }));
        }
        const actualRes = await fetchWithTimeout(`${API_BASE_URL}/api/actual-result?testRunDetailId=${tc.testRunDetailId}`);
        const actualData = await actualRes.json();
        if (!this.modalTc || Number(this.modalTc.testRunDetailId) !== Number(tc.testRunDetailId)) return;
        if (!Array.isArray(actualData) || !actualData.length) return;

        const modeRow = findOverviewActualRow(actualData) || actualData[0];
        this.modalTc.actualResultMode = normalizeActualResultMode(modeRow.actualMode);

        const pendingOverviewImages = (this.modalTc.actualResultImages || []).filter(isPendingUploadImage);
        if (!isByStepMode(this.modalTc.actualResultMode)) {
          const overviewRow = findOverviewActualRow(actualData) || actualData[0];
          this.modalTc.actualResult = overviewRow.actualDetail || this.modalTc.actualResult || "";
          const loadedImages = resolvedActualImages(overviewRow);
          this.modalTc.actualResultImages = [...loadedImages, ...pendingOverviewImages];
          this.jiraIssueForm.description = this.modalTc.actualResult || "";
        }

        const actualByStep = {};
        actualData.forEach((a) => {
          if (a.testStepNo != null) actualByStep[a.testStepNo] = a;
        });
        const existingSteps = this.modalTc.steps || [];
        this.modalTc.steps = existingSteps.map((s, i) => {
          const stepNo = s.stepNo || i + 1;
          const saved = actualByStep[stepNo];
          const pendingStepImages = (s.actualImages || []).filter(isPendingUploadImage);
          const loadedImages = saved ? resolvedActualImages(saved) : (s.actualImages || []).filter((img) => !isPendingUploadImage(img));
          return {
            ...s,
            stepNo,
            actualResult: saved ? (saved.actualDetail || "") : (s.actualResult || ""),
            actualImages: [...loadedImages, ...pendingStepImages],
          };
        });
        if (isByStepMode(this.modalTc.actualResultMode) && Array.isArray(this.modalTc.steps)) {
          const lines = this.modalTc.steps
            .filter((s) => s.actualResult)
            .map((s) => `Step ${s.stepNo}: ${s.actualResult}`);
          if (lines.length) this.jiraIssueForm.description = lines.join("\n\n");
        }
        this.actualResultHistory = this.hydrateHistoryMedia(this.actualResultHistory);
      } catch (e) {
        console.error("load steps error:", e);
      } finally {
        this.loadingSteps = false;
      }
    },
    async loadActualResultHistory(testRunDetailId) {
      const detailId = testRunDetailId || this.modalTc?.testRunDetailId;
      if (!detailId) {
        this.actualResultHistory = [];
        return;
      }
      this.actualResultHistoryLoading = true;
      try {
        this.actualResultHistory = this.hydrateHistoryMedia(await actualResultApi.getHistory(detailId));
      } catch (e) {
        console.error("loadActualResultHistory error:", e);
        this.actualResultHistory = [];
      } finally {
        this.actualResultHistoryLoading = false;
      }
    },
    openActualHistoryTab() {
      this.actualModalSideTab = "history";
      this.loadActualResultHistory();
    },
    formatActivityTime(val) {
      if (!val) return "";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatHistoryValue(fieldName, value) {
      if (Array.isArray(value)) return value.length ? `${value.length} file(s)` : "—";
      if (value == null || value === "" || value === "—") return "—";
      const text = String(value);
      const isLongText = /Actual Result/i.test(fieldName || "");
      if (isLongText && text.length > 120) {
        return `${text.slice(0, 120)}…`;
      }
      return text;
    },
    isHistoryMediaField(name) {
      const n = String(name || "");
      return n === "Media attachments" || / Media$/.test(n);
    },
    isHistoryMediaChange(chg) {
      const name = chg?.fieldName || chg?.field_name;
      if (!this.isHistoryMediaField(name)) return false;
      return this.historyMediaUrls(chg?.oldValue ?? chg?.old_value).length > 0
        || this.historyMediaUrls(chg?.newValue ?? chg?.new_value).length > 0
        || Array.isArray(chg?.oldValue)
        || Array.isArray(chg?.newValue);
    },
    historyMediaUrls(value) {
      if (!Array.isArray(value)) return [];
      return value.filter((item) => typeof item === "string" && /^(https?:|data:)/i.test(item));
    },
    hydrateHistoryMedia(entries) {
      const used = new Set();
      return (Array.isArray(entries) ? entries : []).map((entry) => ({
        ...entry,
        changes: (entry.changes || []).map((chg) => {
          const fieldName = chg.fieldName || chg.field_name;
          const oldValue = chg.oldValue ?? chg.old_value;
          const newValue = chg.newValue ?? chg.new_value;
          const next = { ...chg, fieldName, oldValue, newValue };
          if (!this.isHistoryMediaField(fieldName)) return next;
          const oldUrls = this.historyMediaUrls(oldValue);
          const newUrls = this.historyMediaUrls(newValue);
          if (oldUrls.length || newUrls.length) {
            return { ...next, oldValue: oldUrls, newValue: newUrls };
          }
          const stepMatch = String(fieldName).match(/^Step (\d+) Media$/);
          const current = stepMatch
            ? (((this.modalTc?.steps || []).find((s) => Number(s.stepNo) === Number(stepMatch[1]))?.actualImages) || [])
            : (this.modalTc?.actualResultImages || []);
          const urls = (current || []).filter(Boolean);
          const key = stepMatch ? `step:${stepMatch[1]}` : "overview";
          if (used.has(key) || !urls.length) return next;
          used.add(key);
          return { ...next, oldValue: [], newValue: urls };
        }),
      }));
    },
    historyMediaPreview(value) {
      const all = this.historyMediaUrls(value);
      if (!all.length) return [];
      if (all.length <= 3) {
        return all.map((src, index) => ({ src, index, extra: 0 }));
      }
      return [
        { src: all[0], index: 0, extra: 0 },
        { src: all[1], index: 1, extra: 0 },
        { src: all[2], index: 2, extra: all.length - 2 },
      ];
    },
    openHistoryMedia(value, index) {
      const items = this.historyMediaUrls(value);
      this.lightboxItems = items;
      this.lightboxIndex = Number.isFinite(index) ? index : 0;
      this.syncLightbox();
    },
    async saveActualResult() {
      this.saving = true;
      try {
        const tc = this.modalTc;
        const byStep = isByStepMode(tc.actualResultMode);
        let actualImages = !byStep ? [...(tc.actualResultImages || [])] : [];
        let steps = byStep
          ? tc.steps.map((s) => ({ ...s, actualImages: [...(s.actualImages || [])] }))
          : [];

        const hasNewMedia = (urls) => (urls || []).some((u) => String(u).startsWith("data:"));
        const hasUploadPayload = !byStep
          ? hasNewMedia(actualImages)
          : steps.some((s) => hasNewMedia(s.actualImages));

        if (hasUploadPayload) {
          this.mediaUploadState = {
            scope: "save",
            phase: "compressing",
            fileName: "",
            fileIndex: 1,
            fileTotal: 1,
            percent: null,
          };
        }

        if (actualImages.length) {
          actualImages = await prepareMediaForSave(actualImages);
        }
        if (steps.length) {
          for (const step of steps) {
            if (step.actualImages?.length) {
              step.actualImages = await prepareMediaForSave(step.actualImages);
            }
          }
        }

        const payload = {
          testRunId: tc.testRunId,
          testRunDetailId: tc.testRunDetailId,
          testCaseId: tc.testCaseId,
          runStatus: tc.runStatus,
          actualMode: byStep ? "step" : "overview",
          actualDetail: !byStep ? tc.actualResult || null : null,
          actualImages,
          steps,
          subProject: this.run.subProjectName || "",
          testRunName: this.run.name || "",
          scenario: tc.scenario || "",
          subScenario: tc.subScenario || "",
          changedBy: this.currentUserId,
        };

        let res;
        if (hasUploadPayload) {
          this.mediaUploadState = {
            scope: "save",
            phase: "uploading",
            fileName: "",
            fileIndex: 1,
            fileTotal: 1,
            percent: 0,
          };
          res = await postJsonWithUploadProgress(
            `${API_BASE_URL}/api/actual-result`,
            payload,
            (percent) => {
              this.mediaUploadState = {
                ...this.mediaUploadState,
                percent,
              };
            },
            300000,
          );
        } else {
          res = await fetchWithTimeout(`${API_BASE_URL}/api/actual-result`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          }, 300000);
        }
        const json = await parseApiJsonResponse(res);
        if (json.error) throw new Error(json.error);
        const original = this.allCases.find((c) => c.key === tc.key);
        if (original) {
          original.runStatus = tc.runStatus;
          original.hasActualResult = true;
        }
        this.showActualModal = false;
        this.showToast("success", "Actual result saved.");
      } catch (e) {
        console.error("saveActualResult error:", e);
        this.showToast("error", e.message || "Save failed");
      } finally {
        this.saving = false;
        this.clearMediaUpload();
      }
    },
    async handleStepImage(step, si, event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      const stepKey = this.stepMediaKey(step, si);
      await this.processMediaFiles(files, {
        scope: "step",
        stepKey,
        onEach: (dataUrl) => {
          if (!step.actualImages) step.actualImages = [];
          step.actualImages.push(dataUrl);
        },
      });
      event.target.value = "";
    },
    removeStepImage(step, idx) {
      step.actualImages.splice(idx, 1);
    },
    async handleOverviewImage(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      await this.processMediaFiles(files, {
        scope: "overview",
        onEach: (dataUrl) => {
          this.modalTc.actualResultImages.push(dataUrl);
        },
      });
      event.target.value = "";
    },
    isVideoMediaUrl(url) {
      return isVideoMediaUrl(url);
    },
    openLightbox(src, list) {
      const items = (Array.isArray(list) && list.length ? list : [src]).filter(Boolean);
      this.lightboxItems = items;
      const idx = items.indexOf(src);
      this.lightboxIndex = idx >= 0 ? idx : 0;
      this.syncLightbox();
    },
    closeLightbox() {
      this.lightboxSrc = null;
      this.lightboxIsVideo = false;
      this.lightboxItems = [];
      this.lightboxIndex = 0;
    },
    async refreshActualResultMedia() {
      if (!this.modalTc?.testRunDetailId) return null;
      const res = await fetchWithTimeout(
        `${API_BASE_URL}/api/actual-result?testRunDetailId=${this.modalTc.testRunDetailId}`,
      );
      if (!res.ok) return null;
      return res.json();
    },
    async refreshOverviewImage(idx) {
      const key = `overview-${idx}`;
      if (this.mediaRefreshAttempted[key]) return;
      this.mediaRefreshAttempted[key] = true;
      try {
        const actualData = await this.refreshActualResultMedia();
        if (!actualData?.length) return;
        const overviewRow = findOverviewActualRow(actualData) || actualData[0];
        const fresh = resolvedActualImages(overviewRow);
        if (fresh[idx] && fresh[idx] !== this.modalTc.actualResultImages[idx]) {
          this.modalTc.actualResultImages.splice(idx, 1, fresh[idx]);
        }
      } catch (e) {
        console.warn("refreshOverviewImage failed:", e.message);
      }
    },
    async refreshStepImage(step, si, idx) {
      const key = `step-${step.stepNo || si}-${idx}`;
      if (this.mediaRefreshAttempted[key]) return;
      this.mediaRefreshAttempted[key] = true;
      try {
        const actualData = await this.refreshActualResultMedia();
        if (!actualData?.length) return;
        const saved = actualData.find((a) => Number(a.testStepNo) === Number(step.stepNo || si + 1));
        const fresh = saved ? resolvedActualImages(saved) : [];
        if (fresh[idx] && fresh[idx] !== step.actualImages[idx]) {
          step.actualImages.splice(idx, 1, fresh[idx]);
        }
      } catch (e) {
        console.warn("refreshStepImage failed:", e.message);
      }
    },
    removeOverviewImage(idx) {
      this.modalTc.actualResultImages.splice(idx, 1);
    },
    actualResultBtnLabel(tc) {
      return tc?.hasActualResult ? "Actual Result" : "+ Add The Actual Result";
    },
    resultDisplayLabel(status) {
      const map = {
        Fail: "Failed",
        Pass: "Passed",
        "Pass with Condition": "Pass with Condition",
        Blocked: "Blocked",
        "No Run": "No Run",
        "Not Run": "Not Run",
        "In progress": "In progress",
        "In Progress": "In progress",
      };
      return map[status] || status;
    },
    resultLabelClass(status) {
      const map = {
        "No Run": "result-label-no-run",
        "Not Run": "result-label-not-run",
        Fail: "result-label-fail",
        Failed: "result-label-fail",
        Pass: "result-label-pass",
        "Pass with Condition": "result-label-pass",
        Blocked: "result-label-blocked",
        "In progress": "result-label-in-progress",
        "In Progress": "result-label-in-progress",
      };
      return map[status] || "result-label-no-run";
    },
    actualBtnClass(status) {
      const map = {
        "No Run": "run-actual-btn--no-run",
        "Not Run": "run-actual-btn--not-run",
        Fail: "run-actual-btn--fail",
        Failed: "run-actual-btn--fail",
        Pass: "run-actual-btn--pass",
        "Pass with Condition": "run-actual-btn--pass",
        Blocked: "run-actual-btn--blocked",
        "In progress": "run-actual-btn--in-progress",
        "In Progress": "run-actual-btn--in-progress",
      };
      return map[status] || "run-actual-btn--no-run";
    },
    resultSelectClass(status) {
      const map = { Pass: "result-pass", "Pass with Condition": "result-pass", Fail: "result-fail", Failed: "result-fail", Blocked: "result-blocked" };
      return map[status] || "";
    },
    testTypeToneClass(testType) {
      const t = String(testType || "").trim().toLowerCase();
      if (t === "positive") return "run-type-pill--positive";
      if (t === "negative") return "run-type-pill--negative";
      return "run-type-pill--default";
    },
    reviewTypeToneClass(testType) {
      const v = String(testType || "").trim().toLowerCase();
      if (v === "positive") return "is-positive";
      if (v === "negative") return "is-negative";
      return "";
    },
    reviewPriorityToneClass(priority) {
      const v = String(priority || "").trim().toLowerCase();
      if (v === "high" || v === "critical") return "is-high";
      if (v === "medium") return "is-medium";
      if (v === "low") return "is-low";
      return "";
    },
    enumLabel(value) {
      const raw = String(value || "").trim();
      if (!raw) return "—";
      return raw.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    },
    toggleFunction(key) {
      const idx = this.collapsedFunctions.indexOf(key);
      if (idx === -1) this.collapsedFunctions.push(key);
      else this.collapsedFunctions.splice(idx, 1);
    },
    onFunctionFilterChange() {
      this.filters.scenario = "";
      this.filters.subScenario = "";
    },
    onScenarioFilterChange() {
      this.filters.subScenario = "";
    },
    setRunFilterValue(field, value) {
      this.filters[field] = value;
      if (field === "function") this.onFunctionFilterChange();
      else if (field === "scenario") this.onScenarioFilterChange();
    },
    clearRunFilters() {
      this.search = "";
      this.filters = { function: "", scenario: "", subScenario: "", status: "" };
    },
    scenarioCollapseKey(fn, sc) {
      return `${fn}|${sc}`;
    },
    subScenarioCollapseKey(fn, sc, sub) {
      return `${fn}|${sc}|${sub}`;
    },
    functionCaseCount(fnGroup) {
      return (fnGroup?.scenarioGroups || []).reduce((sum, sc) => sum + this.scenarioCaseCount(sc), 0);
    },
    scenarioCaseCount(scGroup) {
      return (scGroup?.subGroups || []).reduce((sum, sg) => sum + (sg.cases?.length || 0), 0);
    },
    toggleScenario(fnKey, scKey) {
      const key = this.scenarioCollapseKey(fnKey, scKey);
      const idx = this.collapsedScenarios.indexOf(key);
      if (idx === -1) this.collapsedScenarios.push(key);
      else this.collapsedScenarios.splice(idx, 1);
    },
    toggleSubScenario(fnKey, scKey, subKey) {
      const key = this.subScenarioCollapseKey(fnKey, scKey, subKey);
      const idx = this.collapsedSubScenarios.indexOf(key);
      if (idx === -1) this.collapsedSubScenarios.push(key);
      else this.collapsedSubScenarios.splice(idx, 1);
    },
    parseTcJiraLinks(raw) {
      if (!raw) return [];
      try {
        const v = typeof raw === "string" ? raw.trim() : raw;
        if (!v) return [];
        if (typeof v === "string" && v.startsWith("[")) {
          const arr = JSON.parse(v);
          return Array.isArray(arr) ? arr.filter((j) => j && j.key) : [];
        }
        if (typeof v === "string") {
          return [{ key: v.trim().toUpperCase() }];
        }
        if (Array.isArray(v)) return v.filter((j) => j && j.key);
      } catch (_) { /* ignore */ }
      return [];
    },
    async loadModalTcJira(tc) {
      let raw = tc?.jiraLink || null;
      if (tc?.testCaseId) {
        try {
          const res = await fetchWithTimeout(
            `${API_BASE_URL}/api/real-test-cases/${tc.testCaseId}/jira-link`
          );
          if (res.ok) {
            const data = await res.json();
            if (data?.jiraLink) raw = data.jiraLink;
          }
        } catch (e) {
          console.error("loadModalTcJira jira-link error:", e);
        }
      }
      if (this.modalTc) this.modalTc.jiraLink = raw;
      const links = this.parseTcJiraLinks(raw);
      if (links.length === 0) {
        this.modalTcJiraItems = [];
        this.jiraLinkedFromTcItems = [];
        this.loadingJiraLinkedFrom = false;
        this.loadingModalJira = false;
        return;
      }
      this.loadingModalJira = true;
      this.modalTcJiraItems = links.map((l) => ({
        key: l.key,
        summary: "",
        url: l.url || "#",
      }));
      try {
        const enriched = await fetchJiraIssuesByKeys(links.map((l) => l.key));
        this.modalTcJiraItems = enrichJiraLinkRows(
          links.map((l) => ({ key: l.key, summary: "", url: l.url || "#" })),
          enriched
        );
      } catch (e) {
        console.error("loadModalTcJira error:", e);
      } finally {
        this.loadingModalJira = false;
      }
      await this.loadModalTcLinkedFromIssues();
    },
    jiraBrowseUrl(key) {
      const k = String(key || "").trim().toUpperCase();
      if (!k) return "#";
      const fromTc = (this.modalTcJiraItems || []).find((j) => String(j.key).toUpperCase() === k);
      if (fromTc?.url) return fromTc.url;
      const fromLinked = (this.jiraLinkedFromTcItems || []).find((j) => String(j.key).toUpperCase() === k);
      if (fromLinked?.url) return fromLinked.url;
      const sample = this.modalTcJiraItems?.[0]?.url;
      if (sample && sample.includes("/browse/")) {
        return sample.replace(/\/browse\/[^/]+$/i, `/browse/${k}`);
      }
      return "#";
    },
    async loadModalTcLinkedFromIssues() {
      const keys = (this.modalTcJiraItems || []).map((j) => j.key).filter(Boolean);
      if (keys.length === 0) {
        this.jiraLinkedFromTcItems = [];
        this.loadingJiraLinkedFrom = false;
        return;
      }
      const seedSet = new Set(keys.map((k) => String(k).toUpperCase()));
      this.loadingJiraLinkedFrom = true;
      try {
        const res = await fetchWithTimeout(
          `${API_BASE_URL}/api/jira/linked-issues?keys=${encodeURIComponent(keys.join(","))}`
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load linked issues");
        let items = (Array.isArray(data) ? data : []).filter(
          (r) => r?.key && !seedSet.has(String(r.key).toUpperCase())
        );
        if (items.length > 0) {
          const enriched = await fetchJiraIssuesByKeys(items.map((r) => r.key));
          items = enrichJiraLinkRows(
            items.map((r) => ({ ...r, url: r.url || this.jiraBrowseUrl(r.key) })),
            enriched
          );
        }
        this.jiraLinkedFromTcItems = items;
      } catch (e) {
        console.error("loadModalTcLinkedFromIssues error:", e);
        this.jiraLinkedFromTcItems = [];
      } finally {
        this.loadingJiraLinkedFrom = false;
      }
    },
    collectTcImagesForJira(tc) {
      if (!tc) return [];
      const all = isByStepMode(tc.actualResultMode)
        ? (tc.steps || []).flatMap((s) => s.actualImages || [])
        : (tc.actualResultImages || []);
      return all.filter((url) => !isVideoMediaUrl(url));
    },
    handleJiraIssueImage(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.jiraIssueForm.images.push(e.target.result);
        };
        reader.readAsDataURL(file);
      });
      event.target.value = "";
    },
    removeJiraIssueImage(idx) {
      this.jiraIssueForm.images.splice(idx, 1);
    },
    openJiraCreateForm() {
      this.showJiraLinkForm = false;
      this.jiraIssueError = null;
      this.jiraIssueForm.images = [...this.collectTcImagesForJira(this.modalTc)];
      this.showJiraCreateForm = true;
      if (this.jiraCreateMode === "standalone" && !this.jiraSpaces.length) {
        this.loadJiraSpaces();
      }
    },
    closeJiraCreateForm() {
      this.showJiraCreateForm = false;
      this.jiraIssueError = null;
      this.jiraIssueForm.images = [];
      this.jiraIssueForm.spaceKey = null;
      this.jiraIssueForm.relatedTaskKey = null;
      this.activeSpaceSprint = null;
      this.jiraSpaceTasks = [];
    },
    openJiraLinkForm() {
      this.showJiraCreateForm = false;
      this.jiraIssueError = null;
      this.jiraLinkIssueKey = "";
      this.jiraLinkSelectedIssueKey = null;
      this.jiraLinkPreview = null;
      this.jiraLinkPreviewError = null;
      this.jiraLinkSpaceIssues = [];
      this.showJiraLinkForm = true;
      const defaultSpace = this.jiraCreateMode === "legacy"
        ? this.jiraProjectKeyForIssue
        : (this.jiraIssueForm.spaceKey || null);
      if (defaultSpace) {
        this.jiraLinkSpaceKey = defaultSpace;
        this.loadJiraLinkSpaceIssues(defaultSpace);
      }
      if (!this.jiraSpaces.length) {
        this.loadJiraSpaces();
      }
    },
    closeJiraLinkForm() {
      this.showJiraLinkForm = false;
      this.jiraIssueError = null;
      this.jiraLinkIssueKey = "";
      this.jiraLinkSelectedIssueKey = null;
      this.jiraLinkSpaceKey = null;
      this.jiraLinkSpaceIssues = [];
      this.jiraLinkPreview = null;
      this.jiraLinkPreviewError = null;
    },
    onJiraLinkSpaceChange(spaceKey) {
      this.jiraLinkSelectedIssueKey = null;
      this.jiraLinkIssueKey = "";
      this.jiraLinkPreview = null;
      this.jiraLinkPreviewError = null;
      this.jiraLinkSpaceIssues = [];
      if (!spaceKey) return;
      this.loadJiraLinkSpaceIssues(spaceKey);
    },
    onJiraLinkIssuePicked(issueKey) {
      if (!issueKey) {
        this.jiraLinkPreview = null;
        return;
      }
      this.jiraLinkIssueKey = "";
      this.jiraLinkPreviewError = null;
      const picked = this.jiraLinkSpaceIssues.find((i) => i.key === issueKey);
      this.jiraLinkPreview = {
        key: issueKey,
        summary: picked?.summary || null,
        status: picked?.status || null,
      };
    },
    onJiraLinkKeyInput() {
      this.jiraLinkSelectedIssueKey = null;
      this.jiraLinkPreview = null;
      this.jiraLinkPreviewError = null;
    },
    async loadJiraLinkSpaceIssues(projectKey, search = "") {
      this.loadingJiraLinkIssues = true;
      try {
        const pk = encodeURIComponent(String(projectKey).trim());
        const q = search ? `&search=${encodeURIComponent(search)}` : "";
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issues?projectKey=${pk}${q}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load issues");
        this.jiraLinkSpaceIssues = (Array.isArray(data) ? data : []).map((i) => ({
          key: i.key,
          summary: i.summary || "",
          status: i.status || "",
          label: `${i.key} — ${i.summary || "—"}`,
        }));
      } catch (e) {
        console.error("loadJiraLinkSpaceIssues error:", e);
        this.jiraLinkSpaceIssues = [];
      } finally {
        this.loadingJiraLinkIssues = false;
      }
    },
    async previewJiraLinkIssue() {
      if (this.jiraLinkSelectedIssueKey) return;
      const key = String(this.jiraLinkIssueKey || "").trim().toUpperCase();
      this.jiraLinkIssueKey = key;
      if (!key) {
        this.jiraLinkPreview = null;
        this.jiraLinkPreviewError = null;
        return;
      }
      if (!/^[A-Z][A-Z0-9_]*-\d+$/.test(key)) {
        this.jiraLinkPreview = null;
        this.jiraLinkPreviewError = "Invalid issue key format";
        return;
      }
      this.jiraLinkPreviewLoading = true;
      this.jiraLinkPreviewError = null;
      try {
        const enriched = await fetchJiraIssuesByKeys([key]);
        if (!enriched.length) {
          this.jiraLinkPreview = null;
          this.jiraLinkPreviewError = `Issue ${key} not found`;
          return;
        }
        const row = enriched[0];
        this.jiraLinkPreview = {
          key: row.key,
          summary: row.summary || null,
          status: row.status || null,
        };
        if (!this.jiraLinkSpaceKey) {
          this.jiraLinkSpaceKey = key.split("-")[0];
          await this.loadJiraLinkSpaceIssues(this.jiraLinkSpaceKey);
        }
      } catch (e) {
        this.jiraLinkPreview = null;
        this.jiraLinkPreviewError = e.message || "Failed to load issue";
      } finally {
        this.jiraLinkPreviewLoading = false;
      }
    },
    async linkExistingJiraIssue() {
      if (!this.canSubmitJiraLink || this.jiraLinkSubmitting) return;
      this.jiraLinkSubmitting = true;
      this.jiraIssueError = null;
      try {
        const issueKey = this.resolvedJiraLinkIssueKey;
        const tc = this.modalTc;
        const isStandalone = this.jiraCreateMode === "standalone";
        const linkedIssueKeys = isStandalone
          ? []
          : this.modalTcJiraItems.map((j) => j.key);

        if (this.jiraPanelIssueList.some((i) => String(i.key || "").toUpperCase() === issueKey)) {
          throw new Error(`Issue ${issueKey} is already linked`);
        }

        const payload = {
          issueKey,
          testRunDetailId: tc?.testRunDetailId,
          linkedIssueKeys,
          standaloneMode: isStandalone,
          projectKey: this.jiraLinkSpaceKey || issueKey.split("-")[0],
        };
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/link-existing-issue`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const json = await res.json();
        if (!res.ok) {
          throw new Error(typeof json.error === "string" ? json.error : JSON.stringify(json.error || json));
        }

        let linked = {
          key: json.key,
          url: json.url || this.jiraBrowseUrl(json.key),
          issueType: json.issueType || null,
          summary: json.summary || this.jiraLinkPreview?.summary || null,
          status: "",
          transitions: [],
          linkedFromKeys: json.linkedFromKeys || linkedIssueKeys,
          linkErrors: json.linkErrors,
        };
        try {
          const enriched = await fetchJiraIssuesByKeys([json.key]);
          if (enriched.length) {
            [linked] = enrichJiraLinkRows([linked], enriched);
          }
        } catch (e) {
          console.error("enrich linked issue error:", e);
        }
        this.createdJiraIssues.push(linked);
        this.closeJiraLinkForm();
        if (!isStandalone) {
          await this.loadModalTcLinkedFromIssues();
        }
        this.showToast("success", `Linked ${issueKey}`);
      } catch (e) {
        this.jiraIssueError = e.message;
      } finally {
        this.jiraLinkSubmitting = false;
      }
    },
    async loadJiraSpaces() {
      this.loadingJiraSpaces = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/spaces`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load spaces");
        this.jiraSpaces = (Array.isArray(data) ? data : []).map((s) => ({
          key: s.key,
          name: s.name,
          label: `${s.name} (${s.key})`,
        }));
      } catch (e) {
        console.error("loadJiraSpaces error:", e);
        this.jiraSpaces = [];
      } finally {
        this.loadingJiraSpaces = false;
      }
    },
    async onJiraSpaceChange(spaceKey) {
      this.activeSpaceSprint = null;
      this.jiraIssueForm.assigneeAccountId = null;
      this.jiraIssueForm.relatedTaskKey = null;
      this.jiraSpaceTasks = [];
      if (!spaceKey) return;
      await Promise.all([
        this.loadSpaceActiveSprint(spaceKey),
        this.loadAssignableUsers(),
        this.loadJiraSpaceTasks(spaceKey),
      ]);
    },
    async loadJiraSpaceTasks(projectKey, search = "") {
      this.loadingJiraSpaceTasks = true;
      try {
        const pk = encodeURIComponent(String(projectKey).trim());
        const q = search ? `&search=${encodeURIComponent(search)}` : "";
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issues?projectKey=${pk}${q}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load tasks");
        this.jiraSpaceTasks = (Array.isArray(data) ? data : []).map((i) => ({
          key: i.key,
          summary: i.summary || "",
          label: `${i.key} — ${i.summary || "—"}`,
        }));
      } catch (e) {
        console.error("loadJiraSpaceTasks error:", e);
        this.jiraSpaceTasks = [];
      } finally {
        this.loadingJiraSpaceTasks = false;
      }
    },
    async loadSpaceActiveSprint(spaceKey) {
      this.loadingSpaceSprint = true;
      try {
        const pk = encodeURIComponent(String(spaceKey).trim().toUpperCase());
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/spaces/${pk}/active-sprint`);
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load sprint");
        this.activeSpaceSprint = data && data.id ? data : null;
      } catch (e) {
        console.error("loadSpaceActiveSprint error:", e);
        this.activeSpaceSprint = null;
      } finally {
        this.loadingSpaceSprint = false;
      }
    },
    async loadCreatedJiraIssues(testRunDetailId) {
      if (!testRunDetailId) return;
      try {
        const res = await fetchWithTimeout(
          `${API_BASE_URL}/api/test-run-details/${testRunDetailId}/jira-created-issues`,
        );
        if (!res.ok) return;
        const stored = await res.json();
        if (!Array.isArray(stored) || !stored.length) {
          this.createdJiraIssues = [];
          return;
        }
        let items = stored.map((row) => ({
          key: row.key,
          url: this.jiraBrowseUrl(row.key),
          issueType: row.issueType || null,
          summary: row.summary || null,
          linkedFromKeys: row.linkedFromKeys || [],
          status: "",
          transitions: [],
        }));
        const enriched = await fetchJiraIssuesByKeys(items.map((i) => i.key));
        items = enrichJiraLinkRows(items, enriched);
        this.createdJiraIssues = items;
      } catch (e) {
        console.error("loadCreatedJiraIssues error:", e);
        this.createdJiraIssues = [];
      }
    },
    async loadAssignableUsers() {
      const pk = this.jiraEffectiveProjectKey;
      if (!pk) {
        this.assignableUsers = [];
        return;
      }
      this.loadingAssignable = true;
      try {
        const params = new URLSearchParams({ projectKey: pk });
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/assignable-users?${params.toString()}`);
        const data = await res.json();
        if (!res.ok) {
          throw new Error(typeof data.error === "string" ? data.error : JSON.stringify(data.error || data));
        }
        this.assignableUsers = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error("loadAssignableUsers error:", e);
        this.assignableUsers = [];
      } finally {
        this.loadingAssignable = false;
      }
    },
    async createJiraIssue() {
      if (!this.canSubmitJiraIssue) return;
      this.jiraIssueCreating = true;
      this.jiraIssueError = null;
      try {
        const tc = this.modalTc;
        const isByStep = isByStepMode(tc?.actualResultMode);
        const isStandalone = this.jiraCreateMode === "standalone";
        const payload = {
          title: this.jiraIssueForm.title.trim(),
          description: this.jiraIssueForm.description.trim(),
          projectKey: this.jiraEffectiveProjectKey,
          standaloneMode: isStandalone,
          linkedIssueKeys: isStandalone
            ? (this.jiraIssueForm.relatedTaskKey ? [this.jiraIssueForm.relatedTaskKey] : [])
            : this.modalTcJiraItems.map((j) => j.key),
          assigneeAccountId: this.jiraIssueForm.assigneeAccountId || null,
          reporterAccountId: this.user?.jiraAccountId || null,
            testCaseId: tc?.testCaseId,
            testRunId: tc?.testRunId,
          testRunDetailId: tc?.testRunDetailId,
          appBaseUrl: typeof window !== "undefined" ? window.location.origin : null,
          actualMode: isByStep ? "byStep" : "overview",
            actualResult: !isByStep ? (tc?.actualResult || null) : null,
          issueImages: this.jiraIssueForm.images || [],
          steps: isByStep ? (tc?.steps || []).map((s) => ({
              stepNo: s.stepNo,
            actualResult: s.actualResult || "",
            })) : [],
        };
        if (isStandalone) {
          payload.projectKey = this.jiraIssueForm.spaceKey;
        }
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/create-issue`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const json = await res.json();
        if (!res.ok) {
          throw new Error(typeof json.error === "string" ? json.error : JSON.stringify(json.error || json));
        }
        if (json.error) throw new Error(typeof json.error === "string" ? json.error : JSON.stringify(json.error));
        const createdTitle = this.jiraIssueForm.title.trim();
        const relatedKeys = isStandalone
          ? (this.jiraIssueForm.relatedTaskKey ? [this.jiraIssueForm.relatedTaskKey] : [])
          : this.modalTcJiraItems.map((j) => j.key);
        let created = {
          key: json.key,
          url: json.url,
          issueType: json.issueType,
          summary: createdTitle,
          status: "",
          transitions: [],
          linkedFromKeys: relatedKeys,
          linkErrors: json.linkErrors,
          attachErrors: json.attachErrors,
          sprintWarning: json.sprintAssign && !json.sprintAssign.assigned
            ? (json.sprintAssign.error || "Could not add to active sprint")
            : null,
        };
        try {
          const enriched = await fetchJiraIssuesByKeys([json.key]);
          if (enriched.length) {
            [created] = enrichJiraLinkRows([created], enriched);
            created.summary = enriched[0].summary || createdTitle;
          }
      } catch (e) {
          console.error("enrich created issue error:", e);
        }
        this.createdJiraIssues.push(created);
        this.showJiraCreateForm = false;
        this.jiraIssueForm.title = "";
        this.jiraIssueForm.description = "";
        this.jiraIssueForm.assigneeAccountId = null;
        this.jiraIssueForm.images = [];
        this.jiraIssueForm.spaceKey = null;
        this.jiraIssueForm.relatedTaskKey = null;
        this.activeSpaceSprint = null;
        this.jiraSpaceTasks = [];
        if (!isStandalone) {
          await this.loadModalTcLinkedFromIssues();
        }
      } catch (e) {
        this.jiraIssueError = e.message;
      } finally {
        this.jiraIssueCreating = false;
      }
    },
  },
};
</script>

<style scoped>
.run-detail-header {
  background-color: var(--surface-ground, #f4f6f9);
  flex-shrink: 0;
}
.tc-jira-links-box {
  background: #f8fafc;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.75rem 1rem;
}
.tc-jira-links-table th,
.tc-jira-links-table td {
  padding: 0.35rem 0.5rem;
  border-color: #e9ecef;
}
.jira-issue-panel {
  background: #fff8f8;
  border: 1px solid #ff9999;
  border-radius: 10px;
  padding: 1rem 1.1rem;
  margin-top: 1rem;
  box-shadow: 0 1px 2px rgba(255, 153, 153, 0.12);
}
.jira-issue-panel__title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #9e3d3d;
  margin: 0;
}
.jira-issue-panel__project {
  font-weight: 600;
  color: #344767;
  margin-left: 0.25rem;
}
.jira-issue-panel__header {
  margin-bottom: 0.85rem;
}
.jira-issue-panel__error {
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: #ea0606;
}
.jira-issue-form {
  background: #fff;
  border: 1px solid #ffc9c9;
  border-radius: 8px;
  padding: 0.85rem 1rem;
  margin-bottom: 0.85rem;
}
.jira-link-preview {
  padding: 0.5rem 0.75rem;
  background: #f8fafc;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}
.jira-link-or-divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #8392ab;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.jira-link-or-divider::before,
.jira-link-or-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e9ecef;
}
.jira-field-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #67748e;
  margin-bottom: 0.25rem;
  display: block;
}
.jira-field-input {
  font-size: 0.8rem;
}
.jira-btn {
  font-size: 0.75rem;
}
.jira-issue-list {
  background: #fff;
  border: 1px solid #ffc9c9;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 0.75rem;
}
.jira-issue-list--created {
  margin-top: 0.75rem;
}
.jira-issue-list__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 0.85rem;
  background: #ff9999;
  border-bottom: 1px solid #ff8a8a;
}
.jira-issue-list__label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.jira-issue-list__empty {
  padding: 1rem 0.85rem;
  font-size: 0.78rem;
  color: #8392ab;
  text-align: center;
}
.jira-issue-table {
  margin: 0;
  table-layout: fixed;
  width: 100%;
}
.jira-issue-table--linked .jira-col--id { width: 11%; }
.jira-issue-table--linked .jira-col--type { width: 12%; }
.jira-issue-table--linked .jira-col--title { width: 51%; }
.jira-issue-table--linked .jira-col--relates { width: 12%; }
.jira-issue-table--linked .jira-col--status { width: 26%; }
.jira-issue-table--created .jira-col--id { width: 11%; }
.jira-issue-table--created .jira-col--type { width: 12%; }
.jira-issue-table--created .jira-col--title { width: 51%; }
.jira-issue-table--created .jira-col--status { width: 26%; }
.jira-issue-table th {
  font-size: 0.68rem;
  font-weight: 600;
  color: #9e3d3d;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.45rem 0.65rem;
  border-bottom: 1px solid #ffe0e0;
  background: #fff5f5;
}
.jira-issue-table td {
  font-size: 0.78rem;
  color: #344767;
  padding: 0.5rem 0.65rem;
  border-color: #ffe8e8;
  vertical-align: middle;
}
.jira-issue-table .jira-col--id {
  white-space: nowrap;
  font-size: 0.72rem;
}
.jira-issue-table .jira-col--relates {
  white-space: nowrap;
  font-size: 0.72rem;
}
.jira-issue-table .jira-col--status {
  white-space: nowrap;
}
.jira-issue-table tbody tr:last-child td {
  border-bottom: none;
}
.jira-issue-table tbody tr:hover td {
  background: #fff8f8;
}
.jira-issue-link {
  font-size: 0.78rem;
  font-weight: 600;
  color: #5e72e4;
  text-decoration: none;
}
.jira-issue-link:hover {
  color: #324cdd;
  text-decoration: underline;
}
.jira-issue-link--muted {
  font-weight: 500;
  color: #67748e;
}
.jira-issue-link--muted:hover {
  color: #5e72e4;
}
.jira-issue-link--new {
  color: #2dce89;
}
.jira-issue-link--new:hover {
  color: #24a46d;
}
.jira-issue-summary {
  white-space: normal;
  word-break: break-word;
  line-height: 1.35;
}
.jira-linked-status-select {
  font-size: 0.72rem;
  width: 100%;
  max-width: 100%;
  padding: 0.2rem 1.35rem 0.2rem 0.4rem;
  border-color: #ffc9c9;
  color: #344767;
}
.jira-issue-thumb img {
  max-height: 72px;
  max-width: 108px;
  border-radius: 6px;
  object-fit: contain;
  border: 1px solid #ffc9c9;
  cursor: zoom-in;
}
.jira-issue-thumb__remove {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  background: #f5365c;
  border-radius: 50%;
  line-height: 1;
  font-size: 0.6rem;
  color: #fff;
  border: none;
  padding: 0;
  cursor: pointer;
}
.lightbox-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.85);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
}
.lightbox-img {
  max-width: 90vw; max-height: 90vh;
  border-radius: 8px; object-fit: contain;
  box-shadow: 0 8px 40px rgba(0,0,0,0.5);
}
.lightbox-close {
  position: absolute; top: 16px; right: 24px;
  background: none; border: none; color: white;
  font-size: 2rem; cursor: pointer; line-height: 1;
  z-index: 2;
}
.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 42px;
  height: 42px;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  cursor: pointer;
  z-index: 2;
}
.lightbox-prev { left: 18px; }
.lightbox-next { right: 18px; }
.lightbox-count {
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 700;
}
.qa-badge {
  display: inline-block; background: #e8f4fd; color: #0c7a9a;
  border: 1px solid #bee5eb; border-radius: 50px;
  font-size: 0.72rem; font-weight: 600; padding: 0.15rem 0.55rem;
}
.owner-badge {
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.04rem 0.26rem;
  line-height: 1.05;
}
.bo-assignee-pill {
  background: #e8f4fd !important;
  color: #0c7a9a !important;
  border-color: #bee5eb !important;
}
.scenario-header-row td { 
  background-color: #f3f6f9; 
  border-bottom: 1px solid #e9ecef; 
  text-align: left;
  padding-left: 1rem;
}
.sub-scenario-header-row td { 
  background-color: #f8fafc; 
  border-bottom: 1px solid #f0f0f0; 
  text-align: left;
}
.run-detail-page {
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
  box-sizing: border-box;
}
.run-detail-main {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.run-detail-col {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.run-detail-card {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.run-detail-card > .card-header.run-summary-header {
  flex-shrink: 0;
  position: relative;
  overflow: visible;
  z-index: 5;
}
.run-detail-card-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-top: 1.25rem !important;
}
.run-detail-list-shell {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.run-search-bar {
  position: relative;
  flex-shrink: 0;
  min-width: 0;
  padding-top: 0.35rem;
  padding-bottom: 0.65rem;
}
.run-search-bar--collapsed {
  display: none;
}

.run-filter-toolbar {
  display: grid;
  grid-template-columns: minmax(140px, 1.2fr) minmax(100px, 0.85fr) minmax(0, 1fr) minmax(0, 1fr) minmax(90px, 0.75fr) max-content;
  gap: 0.5rem;
  align-items: end;
  width: 100%;
  min-width: 0;
}
.run-filter-field--clear {
  width: max-content;
}
.run-clear-filters-btn {
  height: 31px;
  padding: 0 0.7rem;
  white-space: nowrap;
  font-size: 0.72rem;
}
.run-filter-toggle {
  position: absolute;
  left: 50%;
  bottom: 0;
  transform: translate(-50%, 100%);
  z-index: 6;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}
.run-filter-toggle-tab {
  display: inline-flex;
  align-items: center;
  gap: 0.28rem;
  height: 20px;
  padding: 0 0.7rem 0.05rem;
  border: 1px solid #e9ecef;
  border-top: none;
  border-radius: 0 0 10px 10px;
  background: #fff;
  color: #67748e;
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1;
  white-space: nowrap;
}
.run-filter-toggle-tab i {
  font-size: 0.55rem;
}
.run-filter-toggle:hover .run-filter-toggle-tab {
  color: #344767;
  border-color: #ced4da;
}

.run-filter-search,
.run-filter-field {
  min-width: 0;
}

.run-filter-search-label {
  visibility: hidden;
}

.run-search-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  height: 31px;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  background: #fff;
}

.run-search-input-icon {
  position: absolute;
  left: 0.55rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 0.7rem;
  color: #8392ab;
  pointer-events: none;
}

.run-search-input-field {
  width: 100%;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.75rem;
  color: #344767;
  padding: 0 1.65rem 0 1.65rem;
}

.run-search-input-field::placeholder {
  color: #adb5bd;
}

.run-search-clear-btn {
  position: absolute;
  right: 0.35rem;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #8392ab;
  font-size: 0.62rem;
  line-height: 1;
  cursor: pointer;
}

.run-search-clear-btn:hover {
  color: #344767;
  background: #f1f3f5;
}

.run-filter-dropdown {
  position: relative;
  z-index: 85;
}

.run-filter-dropdown--open {
  z-index: 120;
}

.run-filter-trigger {
  width: 100%;
  height: 31px;
  border: 1px solid #d6d9df;
  border-radius: 8px;
  background: #fff;
  color: #67748e;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.35rem;
  padding: 0 0.5rem;
  cursor: pointer;
}

.run-filter-trigger-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}

.run-filter-trigger i {
  flex-shrink: 0;
  font-size: 0.62rem;
  color: #5c6475;
}

.run-filter-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  min-width: 100%;
  max-height: 240px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 300;
  padding: 0.4rem 0;
}

.run-filter-menu-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.55rem 0.9rem;
  font-size: 0.8rem;
  font-weight: 500;
  color: #344767;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
}

.run-filter-menu-item:hover {
  background: #f8f9fa;
}

.run-search-clear {
  font-size: 0.72rem !important;
  white-space: nowrap;
}

.run-detail-table-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 1rem 0.75rem;
}
.run-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.run-table-body {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
}
.run-table-empty {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  text-align: center;
  background: #fff;
}
.run-table-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem 0.65rem 2.5rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.run-function-row {
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 0.6rem 1rem;
  background: #e8edf5;
  border-bottom: 1px solid #dde2eb;
}
.run-function-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.run-group-count {
  flex-shrink: 0;
  margin-left: 0.35rem;
  font-size: 0.72rem;
  font-weight: 700;
  color: #67748e;
}
.run-scenario-row {
  display: flex;
  align-items: center;
  padding: 0.55rem 1rem 0.55rem 2rem;
  background: #f3f4f6;
  border-bottom: 1px solid #e9ecef;
  border-top: 1px solid #e2e5e9;
}
.run-sub-scenario-row {
  display: flex;
  align-items: center;
  padding: 0.4rem 1rem 0.4rem 4rem;
  background: #fafbfc;
  border-bottom: 1px solid #f1f3f5;
  min-width: 0;
}
.run-sub-scenario-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.run-row {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem 0.75rem 4.25rem;
  border-bottom: 1px solid #f1f3f5;
  transition: background 0.15s;
}
.run-row:last-child { border-bottom: none; }
.run-row:hover { background: #f5f8ff; }
.run-col-id       { flex: 0.85; min-width: 72px; display: flex; flex-direction: column; align-items: flex-start; gap: 0.12rem; }
.run-id-ref {
  font-size: 0.62rem;
  font-weight: 600;
  color: #8392ab;
  line-height: 1.2;
}
.run-col-name     { flex: 3.5; min-width: 0; }
.run-col-type     { flex: 1.2; min-width: 80px; }
.run-col-priority { flex: 1; min-width: 80px; }
.run-col-actual   { flex: 1.4; min-width: 150px; }
.run-col-result   { flex: 1.2; min-width: 90px; }
.run-table-header .run-col-type,
.run-table-header .run-col-priority,
.run-table-header .run-col-actual,
.run-table-header .run-col-result {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.run-row .run-col-type,
.run-row .run-col-priority,
.run-row .run-col-actual,
.run-row .run-col-result {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}
.run-row .run-col-type > *,
.run-row .run-col-priority > *,
.run-row .run-col-actual > *,
.run-row .run-col-result > * {
  text-align: center;
}
.run-actual-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.68rem;
  padding: 0.2rem 0.55rem;
  white-space: nowrap;
}
.run-actual-btn__icon {
  font-size: 0.85rem;
  line-height: 1;
}

.run-detail-table-wrap .run-actual-btn {
  white-space: nowrap !important;
}
.run-detail-table-wrap .result-label,
.run-detail-table-wrap .btn {
  white-space: nowrap !important;
}
.run-type-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  font-size: 0.5rem;
  font-weight: 600;
  line-height: 1.1;
  padding: 0.06rem 0.24rem;
  border: 1px solid transparent;
  flex-shrink: 0;
}
.run-type-pill--positive {
  background: #e8f7ef;
  color: #1e7f46;
  border-color: #bde7cd;
}
.run-type-pill--negative {
  background: #fff4e8;
  color: #b35b00;
  border-color: #ffd8b0;
}
.run-type-pill--default {
  background: #f2f4f7;
  color: #667085;
  border-color: #e4e7ec;
}
.tc-name { font-size: 0.875rem; font-weight: 700; color: #344767; line-height: 1.4; }
.tc-sub  { font-size: 0.78rem; color: #8392ab; margin-top: 2px; line-height: 1.35; }
.step-number {
  flex-shrink: 0;
  margin-right: 0.25rem;
  font-weight: 600;
  color: #5e72e4;
}
.step-text {
  color: #344767;
  white-space: pre-wrap;
  word-break: break-word;
}
.result-pass    { border-color: #00CC00 !important; color: #00CC00 !important; }
.result-fail    { border-color: #f5365c !important; color: #f5365c !important; }
.result-blocked { border-color: #fb6340 !important; color: #fb6340 !important; }
.run-result-select.result-pass :deep(.base-select__trigger) {
  border-color: #00CC00 !important;
  color: #00CC00 !important;
}
.run-result-select.result-fail :deep(.base-select__trigger) {
  border-color: #f5365c !important;
  color: #f5365c !important;
}
.run-result-select.result-blocked :deep(.base-select__trigger) {
  border-color: #fb6340 !important;
  color: #fb6340 !important;
}
.result-label {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.2rem 0.55rem;
  border-radius: 0.375rem;
  border: 1px solid;
  cursor: default;
  white-space: nowrap;
  width: 120px;
  text-align: center;
  box-sizing: border-box;
}
.result-label--pass-condition-font {
  font-size: 0.68rem !important;
}
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 3000;
  display: flex; align-items: center; justify-content: center; padding: 1rem;
}
.modal-box { background: #fff; border-radius: 12px; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
.run-actual-modal-box {
  width: 1320px;
  max-width: 98vw;
  height: 90vh;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.run-actual-modal-header,
.run-actual-modal-controls,
.run-actual-modal-footer {
  flex-shrink: 0;
  background: #fff;
}
.run-actual-modal-controls {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
}
.run-actual-ref-id {
  margin-left: auto;
  font-size: 0.75rem;
  font-weight: 700;
  color: #67748e;
  white-space: nowrap;
}
.run-actual-modal-body {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 1rem 1.5rem;
  -webkit-overflow-scrolling: touch;
}
.run-step-modal-wrap {
  overflow-x: auto;
}
.run-step-modal-table {
  width: 100%;
  table-layout: fixed;
}
.run-step-modal-table th,
.run-step-modal-table td {
  vertical-align: top !important;
  overflow: hidden;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.run-step-modal-table .run-step-col,
.run-step-modal-table .run-expected-col {
  max-width: 0;
}
.run-step-cell-inner {
  min-width: 0;
  max-width: 100%;
  font-size: 0.75rem;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.run-step-cell-inner .bullet-text-list,
.run-step-cell-inner .bullet-text-item,
.run-step-cell-inner .bullet-text-plain,
.run-step-cell-inner > span {
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: pre-wrap;
}
.run-actual-textarea {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  font-size: 0.75rem;
  resize: vertical;
}
.run-step-upload-btn {
  width: auto;
  font-size: 0.62rem !important;
  padding: 0.12rem 0.4rem !important;
  line-height: 1.2;
  cursor: pointer;
}
.summary-card { background: #fff; border-radius: 12px; border: 1px solid #e9ecef; padding: 0.62rem 0.82rem; box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
.summary-card-body { display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; }
.summary-card-label { font-size: 0.72rem; color: #67748e; font-weight: 600; margin: 0; }
.summary-card-value { font-size: 1.25rem; font-weight: 700; margin: 0; line-height: 1; flex-shrink: 0; }
.summary-value-total { color: #344767; }
.summary-value-pass { color: #006400; }
.summary-value-condition { color: #11cdef; }
.summary-value-fail { color: #FF0000; }
.summary-value-norun { color: #adb5bd; }
.run-header-actions {
  margin-left: auto;
}
.run-export-btn {
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  white-space: nowrap;
  width: auto;
  min-width: 0;
  padding: 0.32rem 0.5rem !important;
  line-height: 1.2;
  border-color: #666699 !important;
  color: #666699 !important;
  background-color: #FFFFFF !important;
  box-shadow: none !important;
}
.run-export-btn__icon {
  display: inline-flex;
  align-items: center;
  font-size: 1.05rem;
  line-height: 1;
  vertical-align: middle;
}
.run-export-btn .text-success {
  color: #2dce89 !important;
}
.run-export-btn .text-danger {
  color: #f5365c !important;
}
.run-toast {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 9999;
  max-width: min(520px, 92vw);
  padding: 0.85rem 1.25rem;
  border-radius: 10px;
  font-size: 0.825rem;
  font-weight: 600;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  box-shadow: 0 4px 16px rgba(0,0,0,0.12);
  animation: runToastIn 0.2s ease;
}
.run-toast.success { background: #f0fff4; border: 1px solid #c6f6d5; color: #276749; }
.run-toast.error   { background: #fff5f5; border: 1px solid #fed7d7; color: #c53030; }
@keyframes runToastIn {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
:global(body:has(.modal-overlay) #sidenav-main) { z-index: 1 !important; }
</style>

<style>
html.run-detail-scroll-lock,
html.run-detail-scroll-lock body {
  overflow: hidden;
}
html.run-detail-scroll-lock .main-content {
  overflow: hidden;
  max-height: 100dvh;
}
html.run-detail-scroll-lock .page-view {
  overflow: hidden;
  height: calc(100dvh - var(--app-topbar-height, 4rem));
  max-height: calc(100dvh - var(--app-topbar-height, 4rem));
}

/* Status colors — unscoped so Bootstrap .btn cannot override */
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--no-run,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--no-run:hover,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--no-run:focus,
.run-detail-table-wrap .result-label.result-label-no-run {
  border-color: #666699 !important;
  color: #666699 !important;
  background-color: #FFFFFF !important;
  background-image: none !important;
  box-shadow: none !important;
  opacity: 1 !important;
  transform: none !important;
}

.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--not-run,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--not-run:hover,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--not-run:focus,
.run-detail-table-wrap .result-label.result-label-not-run {
  border-color: #000033 !important;
  color: #000033 !important;
  background-color: #F5F5F5 !important;
  background-image: none !important;
  box-shadow: none !important;
  opacity: 1 !important;
  transform: none !important;
}

.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--pass,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--pass:hover,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--pass:focus,
.run-detail-table-wrap .result-label.result-label-pass {
  border-color: #006400 !important;
  color: #006400 !important;
  background-color: #e6fae6 !important;
  background-image: none !important;
  box-shadow: none !important;
  opacity: 1 !important;
  transform: none !important;
}

.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--fail,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--fail:hover,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--fail:focus,
.run-detail-table-wrap .result-label.result-label-fail {
  border-color: #FF0000 !important;
  color: #FF0000 !important;
  background-color: #FFF0F5 !important;
  background-image: none !important;
  box-shadow: none !important;
  opacity: 1 !important;
  transform: none !important;
}

.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--blocked,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--blocked:hover,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--blocked:focus,
.run-detail-table-wrap .result-label.result-label-blocked {
  border-color: #fb6340 !important;
  color: #fb6340 !important;
  background-color: #FFF5EE !important;
  background-image: none !important;
  box-shadow: none !important;
  opacity: 1 !important;
  transform: none !important;
}

.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--in-progress,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--in-progress:hover,
.run-detail-table-wrap .btn.run-actual-btn.run-actual-btn--in-progress:focus,
.run-detail-table-wrap .result-label.result-label-in-progress {
  border-color: #000080 !important;
  color: #000080 !important;
  background-color: #F0FFFF !important;
  background-image: none !important;
  box-shadow: none !important;
  opacity: 1 !important;
  transform: none !important;
}

.run-media-upload-progress__label {
  line-height: 1.35;
}

.run-media-thumb {
  width: 150px;
  height: 100px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  overflow: hidden;
  position: relative;
  background: #f1f3f5;
  flex-shrink: 0;
}

.run-media-thumb--sm {
  width: 80px;
  height: 50px;
}

.run-media-thumb__image,
.run-media-thumb__video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  cursor: zoom-in;
  background: #f8f9fa;
}

.run-media-thumb__video {
  cursor: pointer;
}

.run-media-thumb__pending {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0.35rem;
  text-align: center;
  gap: 0.2rem;
}

.run-media-thumb__type-icon {
  font-size: 1rem;
  color: #5e72e4;
}

.run-media-thumb--sm .run-media-thumb__type-icon {
  font-size: 0.75rem;
}

.run-media-thumb__status,
.run-media-thumb__percent {
  color: #67748e;
  line-height: 1.2;
}

.run-media-thumb--sm .run-media-thumb__status,
.run-media-thumb--sm .run-media-thumb__percent {
  font-size: 0.58rem;
}

.run-media-thumb__pending .run-media-upload-progress__bar {
  width: 100%;
}

.run-media-thumb__overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #eef1f4;
  color: #67748e;
  gap: 0.2rem;
  pointer-events: none;
}

.run-media-thumb__overlay-icon {
  font-size: 1rem;
  color: #5e72e4;
}

.run-media-thumb--sm .run-media-thumb__overlay-icon {
  font-size: 0.7rem;
}

.run-media-thumb__play-badge {
  position: absolute;
  left: 50%;
  bottom: 6px;
  transform: translateX(-50%);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(52, 71, 103, 0.72);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.55rem;
  pointer-events: none;
}

.run-media-thumb--sm .run-media-thumb__play-badge {
  width: 16px;
  height: 16px;
  font-size: 0.45rem;
  bottom: 3px;
}

.run-media-upload-progress__bar {
  height: 4px;
  margin-top: 0.35rem;
  background: #e9ecef;
  border-radius: 999px;
  overflow: hidden;
}

.run-media-upload-progress--footer {
  flex: 1;
  min-width: 0;
  max-width: 320px;
}

.run-media-upload-progress__fill {
  height: 100%;
  background: #5e72e4;
  border-radius: 999px;
  transition: width 0.15s ease;
}

.run-media-upload-progress__fill--indeterminate {
  width: 100% !important;
  background: linear-gradient(90deg, #c8d0f8 0%, #5e72e4 50%, #c8d0f8 100%);
  background-size: 200% 100%;
  animation: run-media-upload-indeterminate 1.2s linear infinite;
}

@keyframes run-media-upload-indeterminate {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

.run-actual-panel-box {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.tab-shell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.3rem;
  margin-bottom: 0.75rem;
  border-radius: 12px;
  background: transparent;
  flex-shrink: 0;
}

.tab-item {
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #696969;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  padding: 0.55rem 0.9rem;
  line-height: 1.2;
  border: none;
  border-radius: 12px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-item.active {
  color: #ffffff;
  background: #344767;
  box-shadow: 0 6px 14px rgba(52, 71, 103, 0.28);
}

.tp-side-tab-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.run-actual-bystep-tabs {
  display: flex;
  flex-direction: column;
  min-height: 360px;
}
.run-actual-bystep-tabs .tp-side-tab-panel {
  overflow: auto;
}
.run-actual-bystep-tabs .run-actual-history-panel {
  max-height: none;
  min-height: 280px;
}

.run-actual-history-panel {
  display: flex;
  flex-direction: column;
  min-height: 180px;
  max-height: 280px;
}

.run-actual-history-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
}

.activity-history-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.activity-history-item {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.55rem 0.65rem;
}

.activity-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
}

.badge-change {
  background: #fff3cd;
  color: #856404;
}

.activity-text {
  white-space: pre-wrap;
  word-break: break-word;
  color: #344767;
}

.qa-tr-view-overlay {
  position: fixed;
  inset: 0;
  width: 100vw;
  height: 100vh;
  z-index: 5000;
  background: #fff;
}
.qa-tr-view-overlay .qa-tc-review-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.qa-tr-view-overlay .qa-tc-review-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 1.25rem;
  background: #fff;
  border-bottom: 1px solid #e9ecef;
}
.qa-tr-view-overlay .qa-tc-review-filters {
  position: relative;
  z-index: 40;
  overflow: visible;
  display: flex !important;
  flex-wrap: nowrap;
  align-items: flex-end;
  gap: 0.75rem;
}
.qa-tr-view-overlay .qa-tc-review-filter-field {
  overflow: visible;
  min-width: 0;
  flex: 1 1 0;
}
.qa-tr-view-overlay .qa-tc-review-filter-field--search {
  flex: 1.3 1 0;
  grid-column: auto !important;
}
.qa-tr-view-overlay .qa-tc-review-filter-field .base-select--open {
  z-index: 50;
}
.qa-tr-view-overlay .qa-tc-review-filter-field--clear {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}
.qa-tr-view-overlay .qa-tr-view-clear-btn {
  white-space: nowrap;
  height: 32px;
}
.qa-tr-view-overlay .qa-tc-review-body {
  flex: 1;
  min-height: 0;
  display: flex;
  position: relative;
  z-index: 0;
}
.qa-tr-view-overlay .qa-tc-review-doc {
  flex: 1;
  min-width: 0;
  overflow: auto;
  padding: 1.25rem;
}
.qa-tr-view-overlay .qa-tc-review-doc-title,
.qa-tr-view-overlay .qa-tc-review-title-block > p:first-child {
  display: none !important;
}
.qa-tr-view-overlay .qa-tc-review-title-block {
  margin-bottom: 1.25rem;
}
.qa-tr-view-overlay .qa-tc-review-info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  background: #fafafa;
  border: 1px solid #dcdcdc;
  border-radius: 8px;
  padding: 0.75rem 0.9rem;
}
.qa-tr-view-overlay .qa-tc-review-info-grid span {
  display: block;
  font-size: 0.7rem;
  color: #8392ab;
}
.qa-tr-view-overlay .qa-tc-review-info-grid strong {
  display: block;
  font-size: 0.82rem;
  color: #344767;
  margin-top: 0.15rem;
}
.qa-tr-view-overlay .qa-tr-view-result {
  font-weight: 700;
}
.qa-tr-view-overlay .qa-tc-review-mode {
  display: inline-flex;
  align-items: center;
  padding: 3px;
  border: 1px solid #dce1ea;
  border-radius: 8px;
  background: #f8f9fb;
}
.qa-tr-view-overlay .qa-tc-review-mode-btn {
  border: 0;
  background: transparent;
  color: #67748e;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.42rem 0.7rem;
  border-radius: 6px;
  cursor: pointer;
}
.qa-tr-view-overlay .qa-tc-review-mode-btn.active {
  background: #fff;
  color: #344767;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.12);
}
.qa-tr-view-overlay .qa-tr-view-actual-field {
  grid-template-columns: 1fr;
  margin-top: 0.25rem;
}
.qa-tr-view-overlay .qa-tc-pdf-table {
  width: 100%;
  table-layout: fixed;
  font-size: 0.78rem;
  border-collapse: separate;
  border-spacing: 0;
}
.qa-tr-view-overlay .qa-tc-pdf-table thead th {
  position: sticky;
  top: 0;
  z-index: 8;
  background: #344767;
  background-clip: padding-box;
  color: #fff;
  font-weight: 700;
  font-size: 0.72rem;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
}
.qa-tr-view-overlay .qa-tc-pdf-table .qa-tc-pdf-section--sc td {
  position: sticky;
  top: var(--qa-tc-pdf-th, 2.5rem);
  z-index: 7;
  background: #f5f5f5;
}
.qa-tr-view-overlay .qa-tc-pdf-table .qa-tc-pdf-section--sub td {
  position: sticky;
  top: calc(var(--qa-tc-pdf-th, 2.5rem) + var(--qa-tc-pdf-sc, 1.85rem));
  z-index: 6;
  background: #fff;
}
.qa-tr-view-overlay .qa-tc-pdf-table th,
.qa-tr-view-overlay .qa-tc-pdf-table td {
  border: 1px solid #dcdcdc;
  padding: 0.45rem 0.5rem;
  vertical-align: top;
  word-break: break-word;
}
.qa-tr-view-overlay .qa-tc-pdf-section td {
  font-weight: 700;
}
.qa-tr-view-overlay .qa-tc-review-case {
  border: 1px solid #dcdcdc;
  border-radius: 10px;
  padding: 0.85rem;
  margin-bottom: 0.75rem;
}
.qa-tr-view-overlay .qa-tc-review-case-top {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  align-items: flex-start;
}
.qa-tr-view-overlay .qa-tc-review-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.7rem;
}
.qa-tr-view-overlay .qa-tc-review-label {
  font-size: 0.68rem;
  font-weight: 800;
  color: #212529;
  text-transform: uppercase;
  margin-bottom: 0.2rem;
}
.qa-tr-view-overlay .qa-tc-review-value {
  font-size: 0.8rem;
  color: rgb(18, 56, 94);
  white-space: pre-wrap;
  margin: 0;
}
.qa-tr-view-overlay .qa-tc-review-steps {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.78rem;
}
.qa-tr-view-overlay .qa-tc-review-steps th,
.qa-tr-view-overlay .qa-tc-review-steps td {
  border: 1px solid #dcdcdc;
  padding: 0.4rem 0.5rem;
  vertical-align: top;
  overflow-wrap: anywhere;
  word-break: break-word;
}
.qa-tr-view-overlay .qa-tc-review-steps th {
  background: #344767;
  color: #fff;
  font-weight: 700;
}
.qa-tr-view-overlay .qa-tc-review-steps .qa-tc-review-col-no { width: 50px; }
.qa-tr-view-overlay .qa-tc-review-steps .qa-tc-review-col-module { width: 80px; }
.qa-tr-view-overlay .qa-tc-review-steps .qa-tc-review-col-step,
.qa-tr-view-overlay .qa-tc-review-steps .qa-tc-review-col-expected,
.qa-tr-view-overlay .qa-tc-review-steps .qa-tc-review-col-actual {
  width: calc((100% - 130px) / 3);
}
.qa-tr-view-overlay .qa-tr-view-result-head {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
  padding-top: 0.1rem;
}
.qa-tr-view-overlay .qa-tr-view-result-head .qa-tc-review-k {
  font-size: 0.72rem;
  font-weight: 700;
}
.qa-tr-view-overlay .qa-tr-view-result-chip {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  border-radius: 0.375rem;
  border: 1px solid;
  white-space: nowrap;
  min-width: 120px;
  text-align: center;
  box-sizing: border-box;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-pass-condition {
  font-size: 0.68rem;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-no-run {
  color: #666699;
  border-color: #666699;
  background: #fff;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-not-run {
  color: #000033;
  border-color: #000033;
  background: #f5f5f5;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-pass {
  color: #006400;
  border-color: #006400;
  background: #e6fae6;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-fail {
  color: #ff0000;
  border-color: #ff0000;
  background: #fff0f5;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-blocked {
  color: #fb6340;
  border-color: #fb6340;
  background: #fff5ee;
}
.qa-tr-view-overlay .qa-tr-view-result-chip.is-in-progress {
  color: #000080;
  border-color: #000080;
  background: #f0ffff;
}
.qa-tr-view-overlay .qa-tr-view-actual-field {
  grid-template-columns: 1fr;
  margin-top: 0.25rem;
}
.qa-tr-view-overlay .qa-tc-review-status {
  padding: 1.5rem 0.5rem;
  color: #67748e;
  text-align: center;
}
.qa-tr-view-overlay .qa-tr-view-media,
.run-history-media-change .qa-tr-view-media {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.4rem;
}
.qa-tr-view-overlay .qa-tr-view-media-thumb,
.run-history-media-change .qa-tr-view-media-thumb {
  position: relative;
  width: 56px;
  height: 56px;
  min-width: 56px;
  min-height: 56px;
  padding: 0;
  border: 1px solid #dce1ea;
  border-radius: 6px;
  overflow: hidden;
  background: #f1f3f5;
  cursor: zoom-in;
  appearance: none;
  flex: 0 0 56px;
}
.qa-tr-view-overlay .qa-tr-view-media-thumb img,
.qa-tr-view-overlay .qa-tr-view-media-thumb video,
.run-history-media-change .qa-tr-view-media-thumb img,
.run-history-media-change .qa-tr-view-media-thumb video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.qa-tr-view-overlay .qa-tr-view-media-play,
.qa-tr-view-overlay .qa-tr-view-media-more,
.run-history-media-change .qa-tr-view-media-play,
.run-history-media-change .qa-tr-view-media-more {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 700;
  background: rgba(15, 23, 42, 0.42);
}
.qa-tr-view-overlay .qa-tr-view-media-play,
.run-history-media-change .qa-tr-view-media-play {
  font-size: 0.55rem;
}
.run-history-media-change {
  margin-bottom: 0.55rem;
}
.run-history-media-compare {
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  margin-top: 0.25rem;
}
.run-history-media-side {
  min-width: 0;
  flex: 1;
}
</style>
